import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  SquaresFour,
  GameController,
  Package,
  Users,
  Folders,
  Monitor,
  Tag,
  ChartLineUp,
  CurrencyInr,
  Gear,
  MagnifyingGlass,
  Plus,
  PencilSimple,
  Trash,
  Eye,
  Funnel,
  SortAscending,
  DownloadSimple,
  ArrowClockwise,
  Bell,
  UserCircle,
  SignOut,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Warning,
  Info,
  Clock,
  ChatCircle,
  UserGear,
  Pulse,
  CaretRight,
  List,
  Star,
  Copy,
  Lock,
  LockOpen,
  X
} from "@phosphor-icons/react";
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { PlatformBadge, getPlatformAccountType } from "../components/PlatformBadge";
import { PlatformIcon } from "../components/PlatformIcon";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"}/api`;

const PLATFORM_OPTIONS = [
  "Steam",
  "Epic Games",
  "PlayStation",
  "Xbox",
  "Nintendo",
  "EA",
  "Ubisoft",
  "Battle.net",
  "Rockstar Games",
  "GOG"
];

const timeAgo = (dateStr) => {
  if (!dateStr) return "Just now";
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "Yesterday";
  return `${diffDays}d ago`;
};

const Admin = () => {
  const { user, accessToken, logout } = useAuth();
  const navigate = useNavigate();
 
  const longValueStyle = {
    overflowWrap: "anywhere",
    wordBreak: "break-word"
  };

  // Active navigation tab
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Core Data Lists
  const [games, setGames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [contacts, setContacts] = useState([]);
 
  // Settings state
  const [storeSettings, setStoreSettings] = useState({
    store_name: "CG39 Game Store",
    whatsapp_support: "+91 6379490178",
    upi_id: "pandiyarajan39@ptyes",
    upi_qr_url: ""
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Stats Card state
  const [stats, setStats] = useState({ revenue: 0, orders: 0, products: 0, users: 0, rating: 4.8 });

  // Loadings
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingGames, setLoadingGames] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Modals & Drawers
  const [showGameModal, setShowGameModal] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDrawer, setShowOrderDrawer] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [orderActionLoading, setOrderActionLoading] = useState(false);

  // Real Order Notifications (Orders awaiting verification or with submitted UTR)
  const pendingOrders = useMemo(() => {
    return orders.filter(o => 
      o.status === "submitted" || 
      o.status === "pending_payment" || 
      o.status === "pending" ||
      o.payment_status === "verification_pending" ||
      o.payment_status === "rejected" ||
      (o.transaction_id && o.payment_status !== "paid" && o.payment_status !== "verified" && o.status !== "completed" && o.status !== "cancelled")
    );
  }, [orders]);

  const recentNewOrders = useMemo(() => {
    return orders.slice(0, 8);
  }, [orders]);

  // Category Form State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");

  // Game Form State
  const [gameFormData, setGameFormData] = useState({
    title: '',
    description: '',
    steam_price: '',
    price: '',
    category_id: '',
    platform: 'Steam',
    image_url: '',
    is_new: false,
    is_bundle: false,
    in_stock: true,
    display_order: '',
    is_structured: false,
    overview: '',
    story: '',
    gameplay: '',
    features: ['']
  });

  const [uploadingImage, setUploadingImage] = useState(false);

  // Orders Log Search & Filters
  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("ALL");
  const [orderSort, setOrderSort] = useState("newest");

  // Games search & filters
  const [gameSearch, setGameSearch] = useState("");
  const [gamePlatformFilter, setGamePlatformFilter] = useState("ALL");
  const [gameCategoryFilter, setGameCategoryFilter] = useState("ALL");
  const [gameStockFilter, setGameStockFilter] = useState("ALL");
  const [gameSort, setGameSort] = useState("newest");

  // Order update helper states
  const [tempNotes, setTempNotes] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("WhatsApp");
  const [deliveryDetails, setDeliveryDetails] = useState("");

  // Platforms configuration state
  const [platforms, setPlatforms] = useState([]);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);

  // Persistent Activity Logs States
  const [activityLogs, setActivityLogs] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotalPages, setLogTotalPages] = useState(1);
  const [logFilterAction, setLogFilterAction] = useState("");
  const [logFilterResourceType, setLogFilterResourceType] = useState("");
  const [logSearch, setLogSearch] = useState("");

  const fetchActivityLogs = async (page = 1) => {
    try {
      setLoadingLogs(true);
      const params = {
        page,
        limit: 25,
        search: logSearch
      };
      if (logFilterAction) params.action = logFilterAction;
      if (logFilterResourceType) params.resource_type = logFilterResourceType;

      const res = await axios.get(`${API}/admin/activity-logs`, {
        params,
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (res.data) {
        setActivityLogs(res.data.logs || []);
        setLogPage(res.data.pagination.page);
        setLogTotalPages(res.data.pagination.totalPages);
      }
    } catch (err) {
      console.error("Fetch activity logs error:", err);
      toast.error("Unable to load activity logs. Please try again.");
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchPlatforms = useCallback(async () => {
    try {
      setLoadingPlatforms(true);
      const res = await axios.get(`${API}/admin/platforms`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (res.data) {
        setPlatforms(res.data);
      }
    } catch (err) {
      console.error("Fetch platforms error:", err);
    } finally {
      setLoadingPlatforms(false);
    }
  }, [accessToken]);

  const [lockingPlatformId, setLockingPlatformId] = useState(null);

  const handleTogglePlatformLock = async (platform) => {
    try {
      setLockingPlatformId(platform.id);
      const newLockedState = !platform.is_locked;
      
      const res = await axios.patch(
        `${API}/admin/platforms/${platform.id}/lock`,
        { is_locked: newLockedState },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (res.data?.platform) {
        setPlatforms(prev => prev.map(p => p.id === platform.id ? { ...p, ...res.data.platform, product_count: p.product_count } : p));
        toast.success(`Platform ${platform.name} is now ${newLockedState ? "LOCKED (hidden from storefront)" : "UNLOCKED (visible on storefront)"}`);
      }
    } catch (err) {
      console.error("Toggle platform lock error:", err);
      toast.error(err.response?.data?.error || "Failed to update platform lock state");
    } finally {
      setLockingPlatformId(null);
    }
  };

  const logActivity = (action, target, type = "info") => {
    // Audit logging is now managed server-side.
    // Frontend stub preserved as a no-op to ensure zero regression.
  };

  // Refresh all dashboard registries
  // useCallback ensures a stable reference so the auth useEffect below
  // can list it as a dependency without triggering repeated calls.
  const refreshAllData = React.useCallback(() => {
    fetchGames();
    fetchCategories();
    fetchPlatforms();
    fetchStats();
    fetchOrders();
    fetchAdminReviews();
    fetchUsersList();
    fetchContacts();
    fetchSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Verify Admin authorization on mount
  useEffect(() => {
    const ADMIN_EMAIL = "pandiyarajan007123@gmail.com";
    if (!user) {
      navigate("/");
      return;
    }
    const isAdmin = user.email?.toLowerCase() === ADMIN_EMAIL || user.user_metadata?.role === "admin";
    if (!isAdmin) {
      toast.error("Access Denied: Administrator permissions required");
      navigate("/");
      return;
    }

    refreshAllData();
  }, [user, navigate, refreshAllData]);

  // Fetch activity logs automatically when activeTab changes to activity or search changes
  useEffect(() => {
    if (activeTab === "activity") {
      fetchActivityLogs(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, logFilterAction, logFilterResourceType]);

  /* ================= FETCH REGISTRIES ================= */
  const fetchGames = async () => {
    try {
      setLoadingGames(true);
      const res = await axios.get(`${API}/games?include_banners=true`);
      setGames(res.data || []);
    } catch (err) {
      console.error("Games fetch error:", err);
      toast.error("Failed to fetch games catalog");
    } finally {
      setLoadingGames(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const res = await axios.get(`${API}/categories`);
      const cats = res.data || [];
      setCategories(cats);

      // Automatically seed banner categories if missing
      const bannersCat = cats.find(c => c.name.toLowerCase() === "banners");
      const promoCat = cats.find(c => c.name.toLowerCase() === "promo banners");
      
      if (accessToken && (!bannersCat || !promoCat)) {
        let updated = false;
        if (!bannersCat) {
          await axios.post(`${API}/admin/categories`, { name: "Banners" }, {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
          updated = true;
        }
        if (!promoCat) {
          await axios.post(`${API}/admin/categories`, { name: "Promo Banners" }, {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
          updated = true;
        }
        if (updated) {
          const refreshed = await axios.get(`${API}/categories`);
          setCategories(refreshed.data || []);
        }
      }
    } catch (err) {
      console.error("Categories fetch error:", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setStats(res.data || { revenue: 0, orders: 0, products: 0, users: 0, rating: 4.8 });
    } catch (err) {
      console.error("Stats fetch error:", err);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      const res = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setOrders(res.data || []);
    } catch (err) {
      console.error("Orders fetch error:", err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchAdminReviews = async () => {
    try {
      setLoadingReviews(true);
      const res = await axios.get(`${API}/admin/reviews`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setReviews(res.data || []);
    } catch (err) {
      console.error("Reviews fetch error:", err);
    } finally {
      setLoadingReviews(false);
    }
  };
 
  const handleReviewStatusUpdate = async (reviewId, status) => {
    try {
      const rev = reviews.find(r => r.id === reviewId);
      const gameTitle = rev?.games?.title || "Game";
      const customerName = rev?.profiles?.full_name || "Customer";

      const res = await axios.put(`${API}/admin/reviews/${reviewId}/status`, { status }, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (res.status === 200) {
        toast.success(`Review ${status} successfully`);
        logActivity(`Review status updated to ${status}`, `${gameTitle} by ${customerName}`, "success");
        fetchAdminReviews();
      }
    } catch (err) {
      console.error("Error updating review status:", err);
      toast.error(err.response?.data?.error || "Failed to update review status");
    }
  };

  const fetchUsersList = async () => {
    try {
      setLoadingUsers(true);
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setUsersList(res.data || []);
    } catch (err) {
      console.error("Users list fetch error:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const res = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setContacts(res.data || []);
    } catch (err) {
      console.error("Contacts fetch error:", err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      if (res.data) {
        setStoreSettings(res.data);
      }
    } catch (err) {
      console.error("Settings fetch error:", err);
    }
  };

  const handleRefreshLogs = async () => {
    try {
      setLoadingLogs(true);
      await fetchActivityLogs(1);
      toast.success("System activity logs synced successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to sync audit logs.");
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsLoading(true);
      await axios.put(`${API}/admin/settings`, storeSettings, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      toast.success("Store configurations updated successfully!");
      logActivity("Updated Settings", "Store config changes saved", "success");
    } catch (err) {
      console.error("Settings save error:", err);
      toast.error(err.response?.data?.error || "Failed to update settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  /* ================= CATEGORIES CRUD ================= */
  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryName(cat.name);
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      toast.error("Category name is required");
      return;
    }
    try {
      setLoadingCategories(true);
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, { name: categoryName.trim() }, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        toast.success("Category updated successfully");
        logActivity("Updated Category", categoryName.trim(), "success");
      } else {
        await axios.post(`${API}/admin/categories`, { name: categoryName.trim() }, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        toast.success("Category created successfully");
        logActivity("Created Category", categoryName.trim(), "success");
      }
      setCategoryName("");
      setEditingCategory(null);
      setShowCategoryModal(false);
      fetchCategories();
      fetchStats();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save category");
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      setLoadingCategories(true);
      await axios.delete(`${API}/admin/categories/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      toast.success("Category deleted successfully");
      logActivity("Deleted Category", `Category #${id}`, "warning");
      fetchCategories();
      fetchStats();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to delete category");
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File exceeds 5MB size limit.");
      return;
    }

    setUploadingImage(true);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      try {
        const base64Data = reader.result;
        const folder = activeTab === "banners" ? "banners" : "games";
        const res = await axios.post(`${API}/admin/upload`, {
          base64Data,
          fileName: file.name,
          folder: folder
        }, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        
        if (res.data && res.data.url) {
          setGameFormData(prev => ({ ...prev, image_url: res.data.url }));
          toast.success("Image uploaded successfully!");
        } else {
          toast.error("Upload failed. Unexpected response.");
        }
      } catch (err) {
        console.error("Image upload error:", err);
        const errMsg = err.response?.data?.error || "Image upload failed.";
        toast.error(errMsg);
      } finally {
        setUploadingImage(false);
      }
    };
    reader.onerror = () => {
      toast.error("Failed to read image file.");
      setUploadingImage(false);
    };
  };

  /* ================= GAMES CRUD ================= */
  const handleOpenAddGame = () => {
    setEditingGame(null);
    const firstRegularCat = categories.find(c => c.name.toLowerCase() !== "banners" && c.name.toLowerCase() !== "promo banners");
    const steamPlat = platforms.find(p => p.slug === 'steam');
    setGameFormData({
      title: '',
      description: '',
      steam_price: '',
      price: '',
      category_id: firstRegularCat?.id?.toString() || '',
      platform: 'Steam',
      platform_id: steamPlat?.id || '',
      image_url: '',
      is_new: false,
      is_bundle: false,
      in_stock: true,
      display_order: '',
      is_structured: false,
      overview: '',
      story: '',
      gameplay: '',
      features: ['']
    });
    setShowGameModal(true);
  };

  const handleEditGame = (game) => {
    setEditingGame(game);
    
    let isStructured = false;
    let overview = '';
    let story = '';
    let gameplay = '';
    let features = [''];

    if (game.description && game.description.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(game.description);
        isStructured = true;
        overview = parsed.overview || '';
        story = parsed.story || '';
        gameplay = parsed.gameplay || '';
        features = Array.isArray(parsed.features) && parsed.features.length > 0 ? parsed.features : [''];
      } catch (e) {
        // fallback
      }
    }

    setGameFormData({
      title: game.title || '',
      description: game.description || '',
      steam_price: game.steam_price?.toString() || '',
      price: game.price?.toString() || '',
      category_id: game.category_id?.toString() || '',
      platform: game.platform || 'Steam',
      platform_id: game.platform_id || '',
      image_url: game.image_url || '',
      is_new: game.is_new ?? false,
      is_bundle: game.is_bundle ?? false,
      in_stock: game.in_stock ?? true,
      display_order: game.display_order?.toString() || '',
      is_structured: isStructured,
      overview: isStructured ? overview : '',
      story: isStructured ? story : '',
      gameplay: isStructured ? gameplay : '',
      features: features
    });
    setShowGameModal(true);
  };

  const handleSubmitGame = async (e) => {
    e.preventDefault();

    const selectedCat = categories.find(c => c.id.toString() === gameFormData.category_id.toString());
    const isBannerCategory = selectedCat && (
      selectedCat.name.toLowerCase() === "banners" || 
      selectedCat.name.toLowerCase() === "promo banners"
    );

    const finalPriceVal = isBannerCategory ? "0" : gameFormData.price;

    if (!gameFormData.title.trim() || !finalPriceVal || !gameFormData.category_id) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (!isBannerCategory && gameFormData.is_structured && !gameFormData.overview.trim()) {
      toast.error("Overview is required for structured descriptions");
      return;
    }

    try {
      let finalDescription = gameFormData.description.trim();
      if (!isBannerCategory && gameFormData.is_structured) {
        const cleanedFeatures = gameFormData.features
          .map(f => f.trim())
          .filter(f => f.length > 0);

        const structuredObj = {
          overview: gameFormData.overview.trim(),
          story: gameFormData.story.trim(),
          gameplay: gameFormData.gameplay.trim(),
          features: cleanedFeatures
        };
        finalDescription = JSON.stringify(structuredObj);
      }

      const payload = {
        title: gameFormData.title.trim(),
        description: finalDescription,
        steam_price: !isBannerCategory && gameFormData.steam_price ? parseFloat(gameFormData.steam_price) : null,
        price: parseFloat(finalPriceVal),
        category_id: gameFormData.category_id,
        platform: isBannerCategory ? 'Steam' : (gameFormData.platform || 'Steam'),
        platform_id: isBannerCategory ? (platforms.find(p => p.slug === 'steam')?.id || null) : (gameFormData.platform_id || null),
        image_url: gameFormData.image_url.trim(),
        is_new: isBannerCategory ? false : gameFormData.is_new,
        is_bundle: isBannerCategory ? false : gameFormData.is_bundle,
        in_stock: isBannerCategory ? true : gameFormData.in_stock,
        display_order: gameFormData.display_order ? parseInt(gameFormData.display_order) : 0,
      };

      if (editingGame) {
        await axios.put(`${API}/games/${editingGame.id}`, payload, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        toast.success("Saved successfully");
        logActivity("Updated Item", payload.title, "success");
      } else {
        await axios.post(`${API}/games`, payload, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        toast.success("Created successfully");
        logActivity("Created Item", payload.title, "success");
      }
      setShowGameModal(false);
      setEditingGame(null);
      fetchGames();
      fetchStats();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save parameters");
    }
  };

  const handleDeleteGame = async (id) => {
    if (!window.confirm("Are you sure you want to delete this game? This action is permanent.")) return;
    try {
      await axios.delete(`${API}/games/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      toast.success("Game removed from catalog");
      logActivity("Deleted Game", `Game #${id}`, "warning");
      fetchGames();
      fetchStats();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete game");
    }
  };

  /* ================= ORDERS ACTIONS ================= */
  const handleUpdateOrderField = async (orderId, payload) => {
    if (orderActionLoading) return;
    try {
      setOrderActionLoading(true);
      const res = await axios.put(`${API}/admin/orders/${orderId}`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      toast.success("Order status updated successfully");
      
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(prev => ({
          ...prev,
          ...res.data
        }));
      }
      await fetchOrders();
      await fetchStats();
    } catch (err) {
      console.error("Order update error:", err);
      toast.error(err.response?.data?.error || "Failed to update order details");
    } finally {
      setOrderActionLoading(false);
    }
  };

  const handleVerifyPayment = (orderId) => {
    if (!window.confirm("Verify transaction and mark order as PAID / Processing?")) return;
    handleUpdateOrderField(orderId, { payment_status: "verified", status: "processing" });
    logActivity("Verified Payment (Processing)", `Order #${orderId.slice(0,8)}`, "success");
  };

  const handleRejectPayment = (orderId) => {
    if (!window.confirm("Mark payment as REJECTED? The customer will be able to resubmit verification details.")) return;
    handleUpdateOrderField(orderId, { payment_status: "rejected", status: "created" });
    logActivity("Rejected Payment (Pending Payment)", `Order #${orderId.slice(0,8)}`, "warning");
  };

  const handleSaveDeliveryDetails = (orderId) => {
    const newStatus = selectedOrder?.status === "processing" ? "delivery" : selectedOrder?.status;
    handleUpdateOrderField(orderId, {
      status: newStatus,
      delivery_method: deliveryMethod,
      delivery_details: deliveryDetails
    });
    logActivity("Dispatched Credentials", `Order #${orderId.slice(0,8)} (${deliveryMethod})`, "success");
  };

  const handleCompleteOrder = (orderId) => {
    if (!window.confirm("Mark this order as COMPLETED?")) return;
    handleUpdateOrderField(orderId, { status: "completed" });
    logActivity("Completed Order", `Order #${orderId.slice(0,8)}`, "success");
  };

  const handleSaveAdminNotes = (orderId) => {
    handleUpdateOrderField(orderId, {
      admin_notes: tempNotes
    });
    logActivity("Saved Admin Notes", `Order #${orderId.slice(0,8)}`, "info");
  };

  const handleCancelOrder = (orderId) => {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;
    handleUpdateOrderField(orderId, { status: "cancelled" });
    logActivity("Cancelled Order", `Order #${orderId.slice(0,8)}`, "warning");
  };

  const handleOpenOrderDrawer = (order) => {
    setSelectedOrder(order);
    setTempNotes(order.admin_notes || "");
    setDeliveryMethod(order.delivery_method || "WhatsApp");
    setDeliveryDetails(order.delivery_details || "");
    setShowOrderDrawer(true);
  };

  /* ================= REVIEWS ACTIONS ================= */

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Are you sure you want to delete this review comment?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      toast.success("Review deleted");
      fetchAdminReviews();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete review");
    }
  };

  /* ================= COMPUTED / FILTERED DATA ================= */
  const calculatedStats = useMemo(() => {
    const today = new Date().toDateString();
    const todayOrders = orders.filter(o => new Date(o.created_at).toDateString() === today);
    const awaitingVerif = orders.filter(o => o.status === 'submitted' || o.payment_status === 'submitted' || o.payment_status === 'verification_pending');
    const processing = orders.filter(o => o.status === 'processing');
    const todayCompleted = todayOrders.filter(o => ["completed", "paid", "verified", "delivered", "delivery", "processing"].includes(o.status) || ["paid", "verified"].includes(o.payment_status));
    const todayRevSum = todayCompleted.reduce((sum, o) => sum + (o.total_price || 0), 0);

    return {
      todayOrdersCount: todayOrders.length,
      awaitingVerificationCount: awaitingVerif.length,
      processingCount: processing.length,
      todayRevenue: todayRevSum
    };
  }, [orders]);

  // Dynamic Platform counts from real catalog
  const platformCounts = useMemo(() => {
    const map = {};
    PLATFORM_OPTIONS.forEach(p => { map[p] = 0; });
    games.forEach(g => {
      const p = g.platform || "Steam";
      map[p] = (map[p] || 0) + 1;
    });
    return map;
  }, [games]);

  // Dynamic Promotional Deals from real catalog
  const dealsGames = useMemo(() => {
    return games
      .filter(g => g.steam_price && g.steam_price > g.price)
      .sort((a, b) => {
        const discA = (a.steam_price - a.price) / a.steam_price;
        const discB = (b.steam_price - b.price) / b.steam_price;
        return discB - discA;
      });
  }, [games]);

  // Dynamic Best-Selling Games from real orders
  const bestSellingGames = useMemo(() => {
    const counts = {};
    orders.forEach(ord => {
      ord.order_items?.forEach(item => {
        const title = item.games?.title;
        if (!title || title.toLowerCase() === "game title") return;
        const image = item.games?.image_url || "";
        if (!counts[title]) {
          counts[title] = { title, image, count: 0, revenue: 0 };
        }
        counts[title].count += item.quantity || 1;
        counts[title].revenue += (item.price || 0) * (item.quantity || 1);
      });
    });
    return Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [orders]);

  const filteredOrders = useMemo(() => {
    let result = [...orders];

    if (orderStatusFilter !== "ALL") {
      if (orderStatusFilter === "PENDING_PAYMENT") {
        result = result.filter(o => o.status === "pending_payment" || o.payment_status === "pending");
      } else if (orderStatusFilter === "SUBMITTED") {
        result = result.filter(o => o.status === "submitted" || o.payment_status === "submitted" || o.payment_status === "verification_pending");
      } else if (orderStatusFilter === "PAID") {
        result = result.filter(o => o.payment_status === "paid" || o.payment_status === "verified");
      } else if (orderStatusFilter === "PROCESSING") {
        result = result.filter(o => o.status === "processing");
      } else if (orderStatusFilter === "DELIVERED") {
        result = result.filter(o => o.status === "completed" || o.status === "delivery" || o.status === "delivered");
      } else if (orderStatusFilter === "CANCELLED") {
        result = result.filter(o => o.status === "cancelled");
      }
    }

    if (orderSearch.trim()) {
      const q = orderSearch.toLowerCase();
      result = result.filter(o => 
        o.id.toLowerCase().includes(q) ||
        (o.billing_name || "").toLowerCase().includes(q) ||
        (o.profiles?.full_name || "").toLowerCase().includes(q) ||
        (o.billing_email || "").toLowerCase().includes(q) ||
        (o.profiles?.email || "").toLowerCase().includes(q) ||
        (o.transaction_id || "").toLowerCase().includes(q)
      );
    }

    if (orderSort === "newest") {
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (orderSort === "oldest") {
      result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (orderSort === "highest_value") {
      result.sort((a, b) => b.total_price - a.total_price);
    } else if (orderSort === "lowest_value") {
      result.sort((a, b) => a.total_price - b.total_price);
    }

    return result;
  }, [orders, orderStatusFilter, orderSearch, orderSort]);

  // Dynamic Platform counts from real database-driven platforms & games
  const platformProductCounts = useMemo(() => {
    const map = { ALL: games.length };
    platforms.forEach(p => {
      map[p.id] = games.filter(g => 
        g.platform_id === p.id || 
        g.platforms?.id === p.id || 
        (g.platform && g.platform.toLowerCase() === p.name.toLowerCase())
      ).length;
    });
    return map;
  }, [games, platforms]);

  const filteredGames = useMemo(() => {
    let result = [...games];

    // Filter by Platform
    if (gamePlatformFilter !== "ALL") {
      result = result.filter(g => {
        if (g.platform_id === gamePlatformFilter) return true;
        if (g.platforms?.id === gamePlatformFilter) return true;
        if (g.platforms?.name?.toLowerCase() === gamePlatformFilter.toLowerCase()) return true;
        if (g.platform?.toLowerCase() === gamePlatformFilter.toLowerCase()) return true;
        const matchedPlat = platforms.find(p => p.id === gamePlatformFilter);
        if (matchedPlat && (g.platform?.toLowerCase() === matchedPlat.name.toLowerCase() || g.platforms?.name?.toLowerCase() === matchedPlat.name.toLowerCase())) {
          return true;
        }
        return false;
      });
    }

    // Filter by Category/Genre
    if (gameCategoryFilter !== "ALL") {
      result = result.filter(g => g.category_id?.toString() === gameCategoryFilter);
    }

    // Filter by Stock Status
    if (gameStockFilter !== "ALL") {
      const stockBool = gameStockFilter === "IN_STOCK";
      result = result.filter(g => (g.in_stock ?? true) === stockBool);
    }

    // Filter by Search Query
    if (gameSearch.trim()) {
      const q = gameSearch.toLowerCase();
      result = result.filter(g => 
        g.title.toLowerCase().includes(q) ||
        (g.platform || "").toLowerCase().includes(q) ||
        (g.platforms?.name || "").toLowerCase().includes(q) ||
        (g.categories?.name || "").toLowerCase().includes(q) ||
        (g.description || "").toLowerCase().includes(q)
      );
    }

    // Sorting
    if (gameSort === "newest") {
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (gameSort === "oldest") {
      result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (gameSort === "price_asc") {
      result.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
    } else if (gameSort === "price_desc") {
      result.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
    } else if (gameSort === "title_asc") {
      result.sort((a, b) => a.title.localeCompare(b.title));
    } else if (gameSort === "priority") {
      result.sort((a, b) => (a.display_order ?? 9999) - (b.display_order ?? 9999));
    }

    return result;
  }, [games, gamePlatformFilter, gameCategoryFilter, gameStockFilter, gameSearch, gameSort, platforms]);

  const lowStockGames = useMemo(() => {
    return games.filter(g => g.in_stock === false);
  }, [games]);

  const recentOrders = useMemo(() => {
    return orders.slice(0, 5);
  }, [orders]);

  const recentCustomers = useMemo(() => {
    return usersList.slice(0, 5);
  }, [usersList]);

  const adminUsers = useMemo(() => {
    return usersList.filter(u => u.role === "admin");
  }, [usersList]);

  const verifiedPayments = useMemo(() => {
    return orders.filter(o => o.transaction_id);
  }, [orders]);

  // Sidebar navigation elements
  const sidebarItemsStore = [
    { id: "dashboard", label: "Dashboard", icon: <SquaresFour className="w-5 h-5" /> },
    { id: "products", label: "Games", icon: <GameController className="w-5 h-5" /> },
    { id: "banners", label: "Banners", icon: <Monitor className="w-5 h-5" /> },
    { id: "orders", label: "Orders", icon: <Package className="w-5 h-5" /> },
    { id: "reviews", label: "Reviews", icon: <Star className="w-5 h-5" /> },
    { id: "users", label: "Customers", icon: <Users className="w-5 h-5" /> },
    { id: "categories", label: "Categories", icon: <Folders className="w-5 h-5" /> },
    { id: "platforms", label: "Platforms", icon: <Monitor className="w-5 h-5" /> },
    { id: "offers", label: "Offers & Deals", icon: <Tag className="w-5 h-5" /> },
    { id: "payments", label: "Payments", icon: <CurrencyInr className="w-5 h-5" /> },
    { id: "analytics", label: "Analytics", icon: <ChartLineUp className="w-5 h-5" /> },
    { id: "support", label: "Support", icon: <ChatCircle className="w-5 h-5" /> },
    { id: "settings", label: "Settings", icon: <Gear className="w-5 h-5" /> },
  ];

  const sidebarItemsSystem = [
    { id: "activity", label: "Activity Logs", icon: <Pulse className="w-5 h-5" /> },
    { id: "admins", label: "Admin Users", icon: <UserGear className="w-5 h-5" /> },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusBadge = (status) => {
    const st = (status || "").toLowerCase();
    if (st === "completed" || st === "delivered") {
      return <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-50 text-[#16A34A] border border-green-100">Completed</span>;
    }
    if (st === "processing" || st === "paid") {
      return <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-[#2563EB] border border-blue-100">Processing</span>;
    }
    if (st === "submitted") {
      return <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-[#F59E0B] border border-amber-100">Awaiting UTR</span>;
    }
    if (st === "pending" || st === "pending_payment") {
      return <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-600 border border-zinc-200">Pending</span>;
    }
    return <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-50 text-[#DC2626] border border-red-100">{status}</span>;
  };

  const renderSidebarContent = (isMobile = false) => (
    <div className="flex flex-col h-full justify-between select-none">
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-4 flex items-center justify-between border-b border-[#E5E5E5] mb-5 bg-zinc-50/50">
          <div className="flex items-center gap-2">
            <span className="font-black text-[#E10600] text-xl tracking-tighter">CG39</span>
            <span className="h-4 w-px bg-[#E5E5E5]" />
            <span className="text-[10px] text-[#171717] font-bold uppercase tracking-wider">Console</span>
          </div>
          {isMobile && (
            <button 
              onClick={() => setShowMobileSidebar(false)}
              className="p-1 hover:bg-zinc-200 rounded-lg text-zinc-500 hover:text-zinc-800 transition"
              aria-label="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="px-3 space-y-6 pb-6">
          <div>
            <span className="px-4 text-[9px] font-black uppercase text-zinc-400 tracking-widest block mb-2">Store Management</span>
            <nav className="space-y-0.5">
              {sidebarItemsStore.map(item => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => { setActiveTab(item.id); setShowMobileSidebar(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition duration-150 relative ${
                      isActive
                        ? "bg-[#FEF2F2] text-[#E10600] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:bg-[#E10600] before:rounded-r"
                        : "text-[#666666] hover:bg-[#F5F5F5] hover:text-[#171717]"
                    }`}
                  >
                    <span className={isActive ? "text-[#E10600]" : "text-zinc-400"}>{item.icon}</span>
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div>
            <span className="px-4 text-[9px] font-black uppercase text-zinc-400 tracking-widest block mb-2">System</span>
            <nav className="space-y-0.5">
              {sidebarItemsSystem.map(item => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => { setActiveTab(item.id); setShowMobileSidebar(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition duration-150 relative ${
                      isActive
                        ? "bg-[#FEF2F2] text-[#E10600] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:bg-[#E10600] before:rounded-r"
                        : "text-[#666666] hover:bg-[#F5F5F5] hover:text-[#171717]"
                    }`}
                  >
                    <span className={isActive ? "text-[#E10600]" : "text-zinc-400"}>{item.icon}</span>
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      <div className="border-t border-[#E5E5E5] bg-zinc-50">
        {/* ── Admin Profile ── */}
        <div className="px-4 py-3 flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-[#E10600] text-white flex items-center justify-center font-black text-xs uppercase tracking-wider shrink-0">
            {user?.email?.slice(0, 2) || "AD"}
          </div>
          <div className="min-w-0 text-left">
            <p className="text-xs font-black text-[#171717] truncate">Pandiyarajan</p>
            <p className="text-[10px] text-[#666666] uppercase tracking-wider font-bold">Admin</p>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="mx-4 border-t border-[#E5E5E5]" />

        {/* ── Exit Console ── */}
        <div className="px-3 pt-2">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider text-[#555555] hover:bg-[#F5F5F5] hover:text-[#171717] transition duration-150 min-h-[44px]"
            aria-label="Exit Admin Console and return to storefront"
            title="Return to storefront without logging out"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            Exit Console
          </button>
        </div>

        {/* ── Divider ── */}
        <div className="mx-4 border-t border-[#E5E5E5]" />

        {/* ── Logout ── */}
        <div className="px-3 pt-2 pb-3">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider text-[#D00000] hover:bg-[#FFF1F1] transition duration-150 min-h-[44px]"
            aria-label="Sign out of Admin Console"
            title="Sign out"
          >
            <SignOut className="w-4 h-4 shrink-0" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F7F8] text-[#171717] font-sans flex flex-col antialiased">
      
      {/* ── HEADER ── */}
      <header className="h-16 bg-white border-b border-[#E5E5E5] flex items-center justify-between px-4 sm:px-6 sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowMobileSidebar(true)}
            className="md:hidden p-1.5 hover:bg-[#F5F5F5] rounded-lg transition"
            aria-label="Open navigation menu"
          >
            <List className="w-5 h-5 text-zinc-500" />
          </button>
          
          <div className="flex items-center">
            <span className="font-black text-[#E10600] text-lg tracking-tighter">CG39</span>
            <span className="h-4 w-px bg-[#E5E5E5] mx-2.5 hidden sm:inline" />
            <span className="text-[10px] text-[#666666] uppercase font-bold tracking-wider hidden sm:inline">Store Admin Console</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Order Notifications Bell */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-600 hover:text-zinc-900 transition relative flex items-center justify-center cursor-pointer"
              title="Order Notifications"
              aria-label="Order Notifications"
            >
              <Bell className="w-4.5 h-4.5" />
              {pendingOrders.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[#E10600] text-white text-[10px] font-black rounded-full flex items-center justify-center px-1 shadow-xs animate-pulse">
                  {pendingOrders.length}
                </span>
              )}
            </button>

            {/* Notifications Popover Dropdown */}
            {showNotifications && (
              <>
                <div 
                  className="fixed inset-0 z-40 bg-black/25 backdrop-blur-xs sm:bg-transparent sm:backdrop-blur-none" 
                  onClick={() => setShowNotifications(false)} 
                />
                <div className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-[66px] sm:top-full sm:mt-2 w-auto sm:w-96 max-w-[calc(100vw-24px)] bg-white border border-[#E5E5E5] rounded-2xl sm:rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in text-left">
                  <div className="p-3.5 bg-zinc-50 border-b border-[#E5E5E5] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-[#E10600]" weight="bold" />
                      <span className="text-xs font-black uppercase tracking-wider text-zinc-900">Order Notifications</span>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider bg-red-100 text-[#E10600] px-2.5 py-0.5 rounded-full">
                      {pendingOrders.length} Pending
                    </span>
                  </div>

                  <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto divide-y divide-zinc-100">
                    {orders.length === 0 ? (
                      <div className="p-6 text-center text-zinc-400 text-xs font-semibold">
                        No recent orders registered
                      </div>
                    ) : (
                      (pendingOrders.length > 0 ? pendingOrders : orders.slice(0, 8)).map(ord => {
                        const isPending = ord.status === "submitted" || ord.status === "pending" || ord.status === "pending_payment";
                        const customerName = ord.billing_name || ord.profiles?.full_name || "Verified Customer";
                        const gameTitle = ord.order_items?.[0]?.games?.title || (ord.order_items?.length > 1 ? `${ord.order_items[0]?.games?.title} +${ord.order_items.length - 1} more` : "Game Product");
                        const statusLabel = ord.status === "submitted" 
                          ? "Awaiting UTR" 
                          : ord.status === "pending_payment" 
                          ? "Pending Payment" 
                          : ord.status === "processing"
                          ? "Processing"
                          : (ord.status || "NEW").toUpperCase();

                        return (
                          <div
                            key={ord.id}
                            onClick={() => {
                              handleOpenOrderDrawer(ord);
                              setShowNotifications(false);
                            }}
                            className={`p-3.5 hover:bg-zinc-50 transition cursor-pointer flex items-start gap-3 ${
                              isPending ? "bg-red-50/30" : ""
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                              isPending ? "bg-red-100 text-[#E10600]" : "bg-zinc-100 text-zinc-600"
                            }`}>
                              <Package className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-1 mb-0.5">
                                <span className="text-[9px] font-black uppercase tracking-widest text-[#E10600]">
                                  {isPending ? "NEW ORDER" : "ORDER"}
                                </span>
                                <span className="text-[10px] font-mono font-bold text-zinc-500">
                                  #{ord.id.substring(0, 8).toUpperCase()}
                                </span>
                              </div>
                              <h4 className="text-xs font-bold text-zinc-900 truncate">{gameTitle}</h4>
                              <p className="text-[11px] text-zinc-500 font-medium truncate">{customerName}</p>
                              <div className="flex items-center justify-between text-[11px] font-bold mt-1.5 pt-1.5 border-t border-zinc-100">
                                <span className="text-zinc-900 font-black">₹{ord.total_price}</span>
                                <span className="text-[9px] uppercase font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100">
                                  {statusLabel}
                                </span>
                                <span className="text-[10px] text-zinc-400 font-normal">
                                  {timeAgo(ord.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="p-2.5 bg-zinc-50 border-t border-[#E5E5E5] text-center">
                    <button
                      onClick={() => {
                        setActiveTab("orders");
                        setShowNotifications(false);
                      }}
                      className="w-full py-1.5 text-xs font-black uppercase tracking-wider text-[#E10600] hover:text-[#C80500] hover:underline"
                    >
                      View All Orders →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <button
            onClick={() => { refreshAllData(); toast.success("Registry records updated"); }}
            className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-500 hover:text-zinc-900 transition flex items-center justify-center"
            title="Reload Stats"
            aria-label="Refresh Stats"
          >
            <ArrowClockwise className="w-4 h-4" />
          </button>
          <div className="h-6 w-px bg-[#E5E5E5] mx-1" />
          <div className="flex items-center gap-2 pl-1 select-none shrink-0" title="Live System Connect">
            <div className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
            <span className="text-[9px] font-bold text-[#666666] uppercase tracking-wider hidden sm:inline-block">Live Connect</span>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        
        {/* Desktop Left Sticky Sidebar */}
        <aside className="hidden md:block w-60 bg-white border-r border-[#E5E5E5] sticky top-16 h-[calc(100vh-64px)] shrink-0 overflow-hidden">
          {renderSidebarContent(false)}
        </aside>

        {/* ── MAIN CONTENT PANEL ── */}
        <main className="flex-1 p-4 sm:p-6 md:p-8 overflow-x-hidden">
          
          {/* 1. DASHBOARD OVERVIEW */}
          {activeTab === "dashboard" && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-left select-none">
                <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Dashboard Overview</h1>
                <p className="text-xs text-zinc-500 mt-1">Live metrics of your CG39 catalog performance.</p>
              </div>

              {/* LEVEL 3: KPI Metrics Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* REVENUE */}
                <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 flex items-center justify-between shadow-xs relative overflow-hidden">
                  <div className="text-left">
                    <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block mb-1">Total Revenue</span>
                    {loadingStats ? (
                      <div className="h-7 w-24 bg-zinc-100 rounded animate-pulse" />
                    ) : (
                      <h3 className="text-xl font-black text-zinc-950">₹{stats.revenue.toLocaleString()}</h3>
                    )}
                    <span className="text-[10px] text-[#16A34A] font-bold mt-1.5 block">Completed Orders</span>
                  </div>
                  <div className="w-11 h-11 rounded-lg bg-green-50 text-[#16A34A] flex items-center justify-center shrink-0 border border-green-100">
                    <CurrencyInr className="w-5.5 h-5.5" />
                  </div>
                </div>

                {/* ORDERS */}
                <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 flex items-center justify-between shadow-xs relative overflow-hidden">
                  <div className="text-left">
                    <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block mb-1">Total Orders</span>
                    {loadingStats ? (
                      <div className="h-7 w-16 bg-zinc-100 rounded animate-pulse" />
                    ) : (
                      <h3 className="text-xl font-black text-zinc-950">{stats.orders}</h3>
                    )}
                    <span className="text-[10px] text-blue-600 font-bold mt-1.5 block">{calculatedStats.processingCount} Processing</span>
                  </div>
                  <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100">
                    <Package className="w-5.5 h-5.5" />
                  </div>
                </div>

                {/* CUSTOMERS */}
                <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 flex items-center justify-between shadow-xs relative overflow-hidden">
                  <div className="text-left">
                    <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block mb-1">Customers</span>
                    {loadingStats ? (
                      <div className="h-7 w-16 bg-zinc-100 rounded animate-pulse" />
                    ) : (
                      <h3 className="text-xl font-black text-zinc-950">{stats.users}</h3>
                    )}
                    <span className="text-[10px] text-zinc-500 font-bold mt-1.5 block">Registered Profiles</span>
                  </div>
                  <div className="w-11 h-11 rounded-lg bg-zinc-100 text-zinc-600 flex items-center justify-center shrink-0 border border-zinc-200">
                    <Users className="w-5.5 h-5.5" />
                  </div>
                </div>

                {/* CATALOG SIZE */}
                <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 flex items-center justify-between shadow-xs relative overflow-hidden">
                  <div className="text-left">
                    <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block mb-1">Active Catalog</span>
                    {loadingStats ? (
                      <div className="h-7 w-16 bg-zinc-100 rounded animate-pulse" />
                    ) : (
                      <h3 className="text-xl font-black text-zinc-950">{stats.products}</h3>
                    )}
                    <span className="text-[10px] text-zinc-500 font-bold mt-1.5 block">Games Listed</span>
                  </div>
                  <div className="w-11 h-11 rounded-lg bg-red-50 text-[#E10600] flex items-center justify-center shrink-0 border border-red-100">
                    <GameController className="w-5.5 h-5.5" />
                  </div>
                </div>
              </div>

              {/* LEVEL 1 & 2: Main Layout Split */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* COLUMN 1 */}
                <div className="lg:col-span-2 space-y-6">
                  
                  {/* 5. Recent Orders Box */}
                  <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 md:p-6 shadow-xs flex flex-col">
                    <div className="flex justify-between items-center mb-4 pb-3 border-b border-[#E5E5E5] select-none">
                      <h3 className="text-xs font-black uppercase tracking-wider text-zinc-800">Recent Orders</h3>
                      <button onClick={() => setActiveTab("orders")} className="text-[10px] font-bold uppercase tracking-wider text-[#E10600] hover:underline flex items-center gap-1">
                        View Log <CaretRight className="w-3 h-3" />
                      </button>
                    </div>
                    
                    {loadingOrders ? (
                      <div className="space-y-3 my-2">
                        <div className="h-10 bg-zinc-100 rounded-lg animate-pulse" />
                        <div className="h-10 bg-zinc-100 rounded-lg animate-pulse" />
                        <div className="h-10 bg-zinc-100 rounded-lg animate-pulse" />
                      </div>
                    ) : recentOrders.length === 0 ? (
                      <div className="py-8 text-center text-zinc-400 text-xs">No orders registered yet.</div>
                    ) : (
                      <div className="min-w-full">
                        {/* Desktop/Tablet Table */}
                        <div className="hidden sm:block overflow-x-auto">
                          <table className="min-w-full divide-y divide-zinc-100 text-left text-xs">
                            <thead>
                              <tr className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider bg-zinc-50/50">
                                <th className="py-2.5 px-4">Order ID</th>
                                <th className="py-2.5 px-4">Customer</th>
                                <th className="py-2.5 px-4">Amount</th>
                                <th className="py-2.5 px-4">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                              {recentOrders.map(o => (
                                <tr key={o.id} className="hover:bg-zinc-50/60 transition duration-150">
                                  <td className="py-3 px-4 font-mono text-[10px] text-zinc-500 font-medium">#{o.id.slice(0, 8)}</td>
                                  <td className="py-3 px-4 font-bold text-zinc-800">{o.billing_name || o.profiles?.full_name || "Verified Customer"}</td>
                                  <td className="py-3 px-4 font-black text-zinc-900">₹{o.total_price}</td>
                                  <td className="py-3 px-4">{getStatusBadge(o.status)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile Separate Cards */}
                        <div className="flex flex-col gap-2.5 sm:hidden">
                          {recentOrders.map(o => (
                            <div key={o.id} className="bg-zinc-50/70 border border-zinc-200/80 rounded-xl p-3.5 space-y-2 text-xs text-left shadow-2xs">
                              <div className="flex justify-between items-center">
                                <span className="font-mono text-[10px] text-zinc-500 font-bold">#{o.id.slice(0, 8).toUpperCase()}</span>
                                {getStatusBadge(o.status)}
                              </div>
                              <div className="flex justify-between items-end pt-1">
                                <div className="min-w-0 pr-2">
                                  <span className="text-[9px] text-zinc-400 font-bold uppercase block">Customer</span>
                                  <span className="font-bold text-zinc-800 block truncate">{o.billing_name || o.profiles?.full_name || "Verified Customer"}</span>
                                </div>
                                <div className="text-right shrink-0">
                                  <span className="text-[9px] text-zinc-400 font-bold uppercase block">Amount</span>
                                  <span className="font-black text-zinc-950 text-sm block">₹{o.total_price}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 6. Recent Payments Box */}
                  <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 md:p-6 shadow-xs flex flex-col">
                    <div className="flex justify-between items-center mb-4 pb-3 border-b border-[#E5E5E5] select-none">
                      <h3 className="text-xs font-black uppercase tracking-wider text-zinc-800">Recent Payments</h3>
                      <button onClick={() => setActiveTab("payments")} className="text-[10px] font-bold uppercase tracking-wider text-[#E10600] hover:underline flex items-center gap-1">
                        View Registries <CaretRight className="w-3 h-3" />
                      </button>
                    </div>

                    {loadingOrders ? (
                      <div className="space-y-3 my-2">
                        <div className="h-10 bg-zinc-100 rounded-lg animate-pulse" />
                      </div>
                    ) : verifiedPayments.length === 0 ? (
                      <div className="py-8 text-center text-zinc-400 text-xs">No transactions registered yet.</div>
                    ) : (
                      <div className="flex flex-col gap-2.5">
                        {verifiedPayments.slice(0, 5).map(pay => (
                          <div key={pay.id} className="bg-zinc-50/70 border border-zinc-200/80 rounded-xl p-3.5 space-y-2 text-xs text-left shadow-2xs">
                            <div className="flex justify-between items-center">
                              <span className="font-mono text-[9px] text-zinc-400 font-bold">#{pay.id.slice(0, 8).toUpperCase()}</span>
                              <span className={`inline-block text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                pay.payment_status === "paid" ? "bg-green-50 text-green-600 border border-green-100" : "bg-amber-50 text-amber-600 border border-amber-100"
                              }`}>
                                {pay.payment_status || "pending"}
                              </span>
                            </div>
                            <div className="flex justify-between items-end pt-0.5">
                              <div className="min-w-0 pr-2">
                                <span className="font-bold text-zinc-850 block truncate">{pay.billing_name || pay.profiles?.full_name || "Verified Customer"}</span>
                                <span className="font-mono text-[10px] text-zinc-500 block mt-0.5 truncate">UTR: {pay.transaction_id || "--"}</span>
                              </div>
                              <div className="text-right shrink-0">
                                <span className="font-black text-zinc-950 text-sm block">₹{pay.total_price}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 9. Best-Selling Games Box */}
                  <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 md:p-6 shadow-xs flex flex-col">
                    <div className="flex justify-between items-center mb-4 pb-3 border-b border-[#E5E5E5] select-none">
                      <h3 className="text-xs font-black uppercase tracking-wider text-zinc-800">Best-Selling Games</h3>
                      <button onClick={() => setActiveTab("analytics")} className="text-[10px] font-bold uppercase tracking-wider text-[#E10600] hover:underline flex items-center gap-1">
                        View Analytics <CaretRight className="w-3 h-3" />
                      </button>
                    </div>

                    {bestSellingGames.length === 0 ? (
                      <div className="py-8 text-center text-zinc-400 text-xs">No sales recorded yet.</div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {bestSellingGames.slice(0, 4).map((item, idx) => (
                          <div key={idx} className="bg-zinc-50/70 border border-zinc-200/80 rounded-xl p-3 flex items-center gap-3 shadow-2xs">
                            <div className="w-7 h-7 rounded-lg bg-red-50 text-[#E10600] font-black text-xs flex items-center justify-center shrink-0 border border-red-100/50">
                              #{idx + 1}
                            </div>
                            <div className="min-w-0 text-left">
                              <h4 className="font-bold text-zinc-900 text-xs truncate">{item.title}</h4>
                              <span className="text-[9px] text-zinc-500 font-semibold mt-0.5 block">
                                {item.count} sold • ₹{item.revenue}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>

                {/* COLUMN 2 */}
                <div className="space-y-6">
                  
                  {/* 7. Pending Reviews Box */}
                  <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 md:p-6 shadow-xs flex flex-col">
                    <div className="flex justify-between items-center mb-4 pb-3 border-b border-[#E5E5E5] select-none">
                      <h3 className="text-xs font-black uppercase tracking-wider text-zinc-800">Pending Reviews</h3>
                      <button onClick={() => setActiveTab("reviews")} className="text-[10px] font-bold uppercase tracking-wider text-[#E10600] hover:underline flex items-center gap-1">
                        Moderate <CaretRight className="w-3 h-3" />
                      </button>
                    </div>

                    {loadingReviews ? (
                      <div className="space-y-2 my-1">
                        <div className="h-10 bg-zinc-100 rounded-lg animate-pulse" />
                      </div>
                    ) : reviews.filter(r => r.status === "pending").length === 0 ? (
                      <div className="py-6 text-center text-zinc-400 text-xs">No pending reviews.</div>
                    ) : (
                      <div className="flex flex-col gap-2.5">
                        {reviews.filter(r => r.status === "pending").slice(0, 4).map(rev => (
                          <div key={rev.id} className="bg-zinc-50/70 border border-zinc-200/80 rounded-xl p-3 text-xs text-left shadow-2xs">
                            <div className="flex justify-between items-center">
                              <span className="font-bold text-zinc-800 truncate pr-2">{rev.profiles?.full_name || "Verified Customer"}</span>
                              <span className="text-amber-500 font-black text-[10px] shrink-0">★ {rev.rating}</span>
                            </div>
                            <p className="text-zinc-500 line-clamp-1 mt-1 font-medium italic">"{rev.comment}"</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 8. Support Inquiries Box */}
                  <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 md:p-6 shadow-xs flex flex-col">
                    <div className="flex justify-between items-center mb-4 pb-3 border-b border-[#E5E5E5] select-none">
                      <h3 className="text-xs font-black uppercase tracking-wider text-zinc-800">Support Inquiries</h3>
                      <button onClick={() => setActiveTab("support")} className="text-[10px] font-bold uppercase tracking-wider text-[#E10600] hover:underline flex items-center gap-1">
                        View Support <CaretRight className="w-3 h-3" />
                      </button>
                    </div>

                    {loadingContacts ? (
                      <div className="space-y-2 my-1">
                        <div className="h-10 bg-zinc-100 rounded-lg animate-pulse" />
                      </div>
                    ) : contacts.length === 0 ? (
                      <div className="py-6 text-center text-zinc-400 text-xs">No support inquiries.</div>
                    ) : (
                      <div className="flex flex-col gap-2.5">
                        {contacts.slice(0, 4).map(con => (
                          <div key={con.id} className="bg-zinc-50/70 border border-zinc-200/80 rounded-xl p-3 text-xs text-left shadow-2xs">
                            <div className="flex justify-between items-center">
                              <span className="font-bold text-zinc-800 truncate pr-2">{con.name}</span>
                              <span className="text-[9px] text-zinc-400 font-medium shrink-0">{new Date(con.created_at).toLocaleDateString()}</span>
                            </div>
                            <p className="text-zinc-500 line-clamp-1 mt-1 font-medium">"{con.message}"</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Inventory Warnings & New Customers */}
                  <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 md:p-6 shadow-xs flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-center mb-4 pb-3 border-b border-[#E5E5E5] select-none">
                        <h3 className="text-xs font-black uppercase tracking-wider text-zinc-800">Inventory Warnings</h3>
                        <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 bg-red-50 text-red-500 rounded-lg">{lowStockGames.length} Alerts</span>
                      </div>

                      {lowStockGames.length === 0 ? (
                        <div className="py-6 text-center text-zinc-400 text-[11px] leading-relaxed">
                          All listed catalog titles are currently marked in-stock.
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                          {lowStockGames.slice(0, 3).map(game => (
                            <div key={game.id} className="flex items-center gap-3 p-2.5 bg-red-50/50 border border-red-100 rounded-xl text-left shadow-2xs">
                              <img src={game.image_url} alt="" className="w-10 h-7 object-cover rounded-lg bg-zinc-100 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs font-bold text-zinc-800 truncate">{game.title}</p>
                                <span className="text-[9px] font-black uppercase text-red-500 tracking-wider">Out of Stock</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="mt-6 pt-4 border-t border-[#E5E5E5]">
                      <h4 className="text-[10px] font-black uppercase text-zinc-400 tracking-wider mb-3 text-left">New Customers</h4>
                      {loadingUsers ? (
                        <div className="h-10 bg-zinc-100 rounded-lg animate-pulse" />
                      ) : recentCustomers.length === 0 ? (
                        <div className="text-center text-zinc-400 text-xs py-4">No users registered.</div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          {recentCustomers.slice(0, 3).map(cust => (
                            <div key={cust.id} className="bg-zinc-50/70 border border-zinc-200/80 rounded-xl p-2.5 flex items-center justify-between min-w-0 shadow-2xs">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-zinc-200/80 text-zinc-700 flex items-center justify-center font-bold text-[10px] shrink-0 uppercase">
                                  {cust.full_name?.slice(0, 2).toUpperCase() || "VC"}
                                </div>
                                <div className="min-w-0 text-left">
                                  <p className="text-xs font-bold text-zinc-800 truncate">{cust.full_name || "Verified Customer"}</p>
                                  <p className="text-[10px] text-zinc-400 truncate">{cust.email}</p>
                                </div>
                              </div>
                              <span className="text-[9px] text-zinc-400 font-bold shrink-0">{cust.created_at ? new Date(cust.created_at).toLocaleDateString() : "Not specified"}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                </div>

              </div>
            </div>
          )}

          {/* 2. ORDERS LOG */}
          {activeTab === "orders" && (
            <div className="space-y-6 animate-fade-in text-left">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none">
                <div>
                  <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Order Logs</h1>
                  <p className="text-xs text-zinc-500 mt-1">Manage payment validation and digital coordinate delivery.</p>
                </div>
              </div>

              {/* Filters Box */}
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-4 sm:p-5 shadow-xs flex flex-col md:flex-row md:items-center gap-4 justify-between select-none">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 sm:flex-initial">
                    <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input 
                      type="text" 
                      placeholder="Search orders, UTR, client..."
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-xl text-xs font-semibold placeholder-zinc-400 bg-[#F7F7F8] w-full sm:w-64 transition"
                    />
                  </div>
                  
                  <select
                    value={orderStatusFilter}
                    onChange={(e) => setOrderStatusFilter(e.target.value)}
                    className="px-3.5 py-2 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-xl text-xs font-bold uppercase tracking-wider bg-[#F7F7F8]"
                  >
                    <option value="ALL">All Statuses</option>
                    <option value="PENDING_PAYMENT">Pending UTR</option>
                    <option value="SUBMITTED">Submitted UTR</option>
                    <option value="PAID">Paid Only</option>
                    <option value="PROCESSING">Processing</option>
                    <option value="DELIVERED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider">Sort:</span>
                  <select
                    value={orderSort}
                    onChange={(e) => setOrderSort(e.target.value)}
                    className="px-3.5 py-2 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-xl text-xs font-bold uppercase tracking-wider bg-[#F7F7F8]"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="highest_value">Highest Value</option>
                    <option value="lowest_value">Lowest Value</option>
                  </select>
                </div>
              </div>

              {/* Data Section */}
              {loadingOrders ? (
                <div className="py-20 flex justify-center items-center bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">
                  <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="py-20 text-center select-none text-zinc-400 text-xs bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">
                  No orders match current filter parameters.
                </div>
              ) : (
                <>
                  {/* Desktop/Tablet Table Layout */}
                  <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-[#E5E5E5] text-left text-xs">
                        <thead>
                          <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                            <th className="py-3.5 px-6">ID</th>
                            <th className="py-3.5 px-6">Date</th>
                            <th className="py-3.5 px-6">Customer</th>
                            <th className="py-3.5 px-6">Price</th>
                            <th className="py-3.5 px-6">Payment Status</th>
                            <th className="py-3.5 px-6">Order Status</th>
                            <th className="py-3.5 px-6 text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {filteredOrders.map(ord => (
                            <tr key={ord.id} className="hover:bg-zinc-50/50 transition duration-150">
                              <td className="py-4 px-6 font-mono text-[10px] text-zinc-500 font-bold">#{ord.id.slice(0, 8)}</td>
                              <td className="py-4 px-6 text-zinc-500 font-medium">
                                {new Date(ord.created_at).toLocaleDateString("en-IN", {
                                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                                })}
                              </td>
                              <td className="py-4 px-6">
                                <p className="font-bold text-zinc-800">{ord.billing_name || ord.profiles?.full_name}</p>
                                <p className="text-[10px] text-zinc-400 font-medium">{ord.billing_email || ord.profiles?.email}</p>
                              </td>
                              <td className="py-4 px-6 font-black text-zinc-900 text-sm">₹{ord.total_price}</td>
                              <td className="py-4 px-6">
                                <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                  ord.payment_status === "paid" 
                                    ? "bg-green-50 text-[#16A34A] border border-green-100" 
                                    : ord.payment_status === "failed" 
                                    ? "bg-red-50 text-[#DC2626] border border-red-100" 
                                    : "bg-zinc-100 text-zinc-600 border border-zinc-200"
                                }`}>
                                  {ord.payment_status || "pending"}
                                </span>
                              </td>
                              <td className="py-4 px-6">{getStatusBadge(ord.status)}</td>
                              <td className="py-4 px-6 text-center">
                                <button 
                                  onClick={() => handleOpenOrderDrawer(ord)}
                                  className="px-3.5 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 hover:text-zinc-900 rounded-lg text-[10px] font-bold uppercase tracking-wider transition inline-flex items-center gap-1 cursor-pointer"
                                >
                                  <Eye className="w-3.5 h-3.5" /> Details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Separate Cards Layout */}
                  <div className="flex flex-col gap-3.5 md:hidden">
                    {filteredOrders.map(ord => {
                      const dateStr = new Date(ord.created_at).toLocaleDateString("en-IN", {
                        day: 'numeric', month: 'short', year: 'numeric'
                      });
                      const gamesList = ord.order_items?.map(item => item.games?.title).filter(Boolean).join(" + ") || "Unknown Title";
                      const customerName = ord.billing_name || ord.profiles?.full_name || "Verified Customer";
                      return (
                        <div key={ord.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs text-xs text-left space-y-3.5">
                          <div className="flex justify-between items-center border-b border-zinc-100 pb-2.5">
                            <span className="font-mono text-[10px] text-zinc-500 font-bold">ORDER #{ord.id.substring(0, 8).toUpperCase()}</span>
                            <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                              ord.payment_status === "paid" 
                                ? "bg-green-50 text-[#16A34A] border border-green-100" 
                                : ord.payment_status === "failed" 
                                ? "bg-red-50 text-[#DC2626] border border-red-100" 
                                : "bg-zinc-100 text-zinc-600 border border-zinc-200"
                            }`}>
                              {ord.payment_status?.toUpperCase() || "PENDING"}
                            </span>
                          </div>
                          
                          <div className="space-y-2.5">
                            <div>
                              <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">CUSTOMER</span>
                              <span className="font-bold text-zinc-950 block">{customerName}</span>
                              {ord.billing_email && <span className="text-[10px] text-zinc-400 block truncate">{ord.billing_email}</span>}
                            </div>
                            
                            <div>
                              <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">GAMES</span>
                              <span className="font-bold text-zinc-950 block break-words">{gamesList}</span>
                            </div>

                            <div className="grid grid-cols-2 gap-3 bg-zinc-50 border border-zinc-100 rounded-xl p-3">
                              <div>
                                <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">TOTAL</span>
                                <span className="font-black text-zinc-950 text-sm">₹{ord.total_price}</span>
                              </div>
                              <div>
                                <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">DATE</span>
                                <span className="font-bold text-zinc-800 block">{dateStr}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="border-t border-zinc-100 pt-3 flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[9px] font-bold uppercase text-zinc-400">Status:</span>
                              {getStatusBadge(ord.status)}
                            </div>
                            <button 
                              onClick={() => handleOpenOrderDrawer(ord)}
                              className="px-3.5 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-bold uppercase text-[10px] tracking-wider rounded-lg flex items-center gap-1 cursor-pointer transition"
                            >
                              <Eye className="w-3.5 h-3.5" /> View Order
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* 3. GAMES CATALOG */}
          {activeTab === "products" && (
            <div className="space-y-6 animate-fade-in text-left">
              {/* Header & Add Game Button */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none">
                <div>
                  <div className="flex items-center gap-2.5">
                    <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Game Catalog</h1>
                    <span className="px-2.5 py-0.5 rounded-full bg-zinc-100 text-zinc-700 font-extrabold text-xs">
                      {filteredGames.length} {filteredGames.length === 1 ? "Game" : "Games"}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">Manage game parameters, pricing, platform accounts, and stock status.</p>
                </div>
                <button
                  onClick={handleOpenAddGame}
                  className="bg-[#E10600] hover:bg-[#C80500] text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all hover:shadow-md flex items-center justify-center gap-1.5 shadow-sm cursor-pointer min-h-[42px]"
                >
                  <Plus className="w-4 h-4" weight="bold" /> Add Game
                </button>
              </div>

              {/* Platform Categories Pill Bar */}
              <div className="space-y-2 select-none">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase text-zinc-500 tracking-wider">Platform Categories</span>
                  <span className="text-[10px] font-bold text-zinc-500">{platforms.length} Platforms Configured</span>
                </div>
                
                <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none -mx-1 px-1">
                  {/* All Platforms Chip */}
                  <button
                    type="button"
                    onClick={() => setGamePlatformFilter("ALL")}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition shrink-0 cursor-pointer ${
                      gamePlatformFilter === "ALL"
                        ? "bg-[#E10600] text-white shadow-xs"
                        : "bg-white border border-[#E5E5E5] text-zinc-900 hover:bg-zinc-50 hover:border-zinc-300"
                    }`}
                  >
                    <span className={gamePlatformFilter === "ALL" ? "text-white font-bold" : "text-zinc-900 font-bold"}>All Platforms</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                      gamePlatformFilter === "ALL" ? "bg-white/25 text-white" : "bg-zinc-100 text-zinc-700"
                    }`}>
                      {games.length}
                    </span>
                  </button>

                  {/* Platform Specific Chips */}
                  {platforms.map(plat => {
                    const isSelected = gamePlatformFilter === plat.id || gamePlatformFilter === plat.name;
                    const count = platformProductCounts[plat.id] || 0;
                    const isLocked = plat.is_locked;

                    return (
                      <button
                        key={plat.id}
                        type="button"
                        onClick={() => setGamePlatformFilter(plat.id)}
                        className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition shrink-0 cursor-pointer ${
                          isSelected
                            ? "bg-[#E10600] text-white shadow-xs"
                            : "bg-white border border-[#E5E5E5] text-zinc-900 hover:bg-zinc-50 hover:border-zinc-300"
                        }`}
                      >
                        <PlatformIcon platform={plat.name} size={16} className={isSelected ? "text-white" : "text-zinc-700"} />
                        <span className={isSelected ? "text-white font-bold" : "text-zinc-900 font-bold"}>{plat.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                          isSelected ? "bg-white/25 text-white" : "bg-zinc-100 text-zinc-700"
                        }`}>
                          {count}
                        </span>
                        {isLocked && (
                          <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded ${
                            isSelected ? "bg-black/30 text-white" : "bg-red-50 text-[#E10600] border border-red-100"
                          }`}>
                            🔒 Locked
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Filters Bar */}
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-3.5 justify-between select-none shadow-xs">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap items-center gap-3">
                  {/* Search input */}
                  <div className="relative sm:col-span-2 lg:col-span-1">
                    <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input 
                      type="text" 
                      placeholder="Search game catalog..."
                      value={gameSearch}
                      onChange={(e) => setGameSearch(e.target.value)}
                      className="w-full lg:w-64 pl-10 pr-8 py-2.5 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-xl text-xs font-semibold placeholder-zinc-400 bg-[#F7F7F8] transition"
                    />
                    {gameSearch && (
                      <button
                        onClick={() => setGameSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Platform Dropdown Filter */}
                  <select
                    value={gamePlatformFilter}
                    onChange={(e) => setGamePlatformFilter(e.target.value)}
                    className="px-3.5 py-2.5 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-xl text-xs font-bold uppercase tracking-wider bg-[#F7F7F8]"
                  >
                    <option value="ALL">All Platforms ({games.length})</option>
                    {platforms.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({platformProductCounts[p.id] || 0}){p.is_locked ? " [LOCKED]" : ""}
                      </option>
                    ))}
                  </select>

                  {/* Genre Filter */}
                  <select
                    value={gameCategoryFilter}
                    onChange={(e) => setGameCategoryFilter(e.target.value)}
                    className="px-3.5 py-2.5 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-xl text-xs font-bold uppercase tracking-wider bg-[#F7F7F8]"
                  >
                    <option value="ALL">All Genres</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id.toString()}>{c.name}</option>
                    ))}
                  </select>

                  {/* Stock Status Filter */}
                  <select
                    value={gameStockFilter}
                    onChange={(e) => setGameStockFilter(e.target.value)}
                    className="px-3.5 py-2.5 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-xl text-xs font-bold uppercase tracking-wider bg-[#F7F7F8]"
                  >
                    <option value="ALL">All Stock Status</option>
                    <option value="IN_STOCK">In Stock Only</option>
                    <option value="OUT_OF_STOCK">Out of Stock</option>
                  </select>
                </div>

                {/* Sort Dropdown */}
                <div className="flex items-center gap-2 justify-between sm:justify-start pt-2 lg:pt-0 border-t lg:border-t-0 border-zinc-100">
                  <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider shrink-0">Sort:</span>
                  <select
                    value={gameSort}
                    onChange={(e) => setGameSort(e.target.value)}
                    className="w-full sm:w-auto px-3.5 py-2.5 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-xl text-xs font-bold uppercase tracking-wider bg-[#F7F7F8]"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="title_asc">Title: A to Z</option>
                    <option value="priority">Priority / Display Order</option>
                  </select>
                </div>
              </div>

              {/* Total Games Count Bar */}
              <div className="flex items-center justify-between text-xs font-bold text-zinc-500 px-0.5 select-none">
                <span>TOTAL GAMES: <strong className="text-zinc-900 font-black">{filteredGames.length}</strong></span>
                {filteredGames.length !== games.length && (
                  <span className="text-[11px] text-zinc-400">
                    Filtered from {games.length} total products
                  </span>
                )}
              </div>

              {/* Data Table / Separate Cards */}
              {loadingGames ? (
                <div className="bg-white border border-[#E5E5E5] rounded-2xl py-20 flex justify-center items-center shadow-xs">
                  <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                </div>
              ) : filteredGames.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] rounded-2xl py-20 text-center select-none text-zinc-400 text-xs space-y-2 shadow-xs">
                  <p className="font-bold text-zinc-600">No games match current filter parameters.</p>
                  <p className="text-[11px] text-zinc-400">Try adjusting your search query, platform category, or stock status.</p>
                  {(gameSearch || gamePlatformFilter !== "ALL" || gameCategoryFilter !== "ALL" || gameStockFilter !== "ALL") && (
                    <button
                      onClick={() => {
                        setGameSearch("");
                        setGamePlatformFilter("ALL");
                        setGameCategoryFilter("ALL");
                        setGameStockFilter("ALL");
                      }}
                      className="mt-2 text-[#E10600] font-bold text-xs hover:underline cursor-pointer"
                    >
                      Reset All Filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Desktop/Tablet Table Layout */}
                  <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                    <table className="min-w-full divide-y divide-[#E5E5E5] text-left text-xs">
                      <thead>
                        <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                          <th className="py-3.5 px-6">Image</th>
                          <th className="py-3.5 px-6">Game & Platform</th>
                          <th className="py-3.5 px-6">Genre</th>
                          <th className="py-3.5 px-6">Price</th>
                          <th className="py-3.5 px-6">Steam Price</th>
                          <th className="py-3.5 px-6">Stock Status</th>
                          <th className="py-3.5 px-6 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {filteredGames.map(game => {
                          const disc = game.steam_price > game.price 
                            ? Math.round(((game.steam_price - game.price) / game.steam_price) * 100) 
                            : 0;
                          const platName = game.platforms?.name || game.platform || "Steam";
                          const isPlatformLocked = game.platforms?.is_locked;

                          return (
                            <tr key={game.id} className="hover:bg-zinc-50/70 transition duration-150">
                              <td className="py-4 px-6 shrink-0 select-none">
                                <img 
                                  src={game.image_url || "/placeholder.jpg"} 
                                  alt={game.title} 
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&w=300&q=80";
                                  }}
                                  className="w-12 h-9 object-cover rounded-lg bg-zinc-100 border border-zinc-200/60 shadow-2xs" 
                                />
                              </td>
                              <td className="py-4 px-6">
                                <span className="font-bold text-zinc-900 text-sm block">{game.title}</span>
                                <div className="flex items-center gap-2 mt-1 select-none flex-wrap">
                                  <PlatformBadge platform={platName} showName={true} className="scale-85 origin-left" />
                                  <span className="text-zinc-300">•</span>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                                    {game.platforms?.account_type_label || getPlatformAccountType(platName)}
                                  </span>
                                  <span className="text-zinc-300">•</span>
                                  <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400">Order: {game.display_order ?? 0}</span>
                                  {isPlatformLocked && (
                                    <span className="px-1.5 py-0.5 rounded bg-red-50 text-[#E10600] border border-red-100 font-black text-[8px] uppercase tracking-wider">
                                      🔒 Locked Platform
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-4 px-6 text-zinc-600 font-bold uppercase tracking-wider text-[10px]">
                                {game.categories?.name || "Uncategorized"}
                              </td>
                              <td className="py-4 px-6 font-black text-zinc-900 text-sm">₹{game.price}</td>
                              <td className="py-4 px-6 text-zinc-400">
                                {game.steam_price ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="line-through">₹{game.steam_price}</span>
                                    {disc > 0 && <span className="text-[#E10600] font-bold text-[10px]">-{disc}%</span>}
                                  </div>
                                ) : "--"}
                              </td>
                              <td className="py-4 px-6">
                                <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                  game.in_stock !== false 
                                    ? "bg-green-50 text-[#16A34A] border border-green-100" 
                                    : "bg-red-50 text-[#DC2626] border border-red-100"
                                }`}>
                                  {game.in_stock !== false ? "In Stock" : "Out of Stock"}
                                </span>
                              </td>
                              <td className="py-4 px-6 text-center">
                                <div className="flex justify-center items-center gap-1.5">
                                  <button 
                                    onClick={() => window.open(`/game/${game.id}`, '_blank')}
                                    className="p-2 hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 rounded-lg transition border border-zinc-200 bg-white cursor-pointer"
                                    title="View Product"
                                    aria-label="View game"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button 
                                    onClick={() => handleEditGame(game)}
                                    className="p-2 hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 rounded-lg transition border border-zinc-200 bg-white cursor-pointer"
                                    title="Edit parameters"
                                    aria-label="Edit game"
                                  >
                                    <PencilSimple className="w-4 h-4" />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteGame(game.id)}
                                    className="p-2 hover:bg-red-50 text-red-500 hover:text-red-700 rounded-lg transition border border-red-100 bg-red-50/20 cursor-pointer"
                                    title="Delete product"
                                    aria-label="Delete game"
                                  >
                                    <Trash className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Standalone Separate Cards Layout (320px to 430px) */}
                  <div className="flex flex-col gap-3.5 md:hidden">
                    {filteredGames.map(game => {
                      const disc = game.steam_price > game.price 
                        ? Math.round(((game.steam_price - game.price) / game.steam_price) * 100) 
                        : 0;
                      const platName = game.platforms?.name || game.platform || "Steam";
                      const isPlatformLocked = game.platforms?.is_locked;
                      const accountTypeLabel = (game.platforms?.account_type_label || getPlatformAccountType(platName)).toUpperCase();

                      return (
                        <div 
                          key={game.id} 
                          className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs space-y-3 text-xs text-left"
                        >
                          {/* Card Top: Game Image + Details */}
                          <div className="flex gap-3 items-start">
                            <div className="w-16 h-16 rounded-xl bg-zinc-100 border border-zinc-200/70 shrink-0 overflow-hidden relative shadow-2xs">
                              <img 
                                src={game.image_url || "/placeholder.jpg"} 
                                alt={game.title} 
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&w=300&q=80";
                                }}
                                className="w-full h-full object-cover" 
                              />
                            </div>
                            <div className="flex-1 min-w-0 space-y-1">
                              <h4 className="font-bold text-zinc-950 text-sm truncate leading-tight" title={game.title}>
                                {game.title}
                              </h4>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <PlatformBadge platform={platName} showName={true} className="scale-90 origin-left" />
                              </div>
                              <p className="text-zinc-600 font-bold uppercase text-[9px] tracking-wider">
                                {accountTypeLabel}
                              </p>
                            </div>
                          </div>

                          {/* Card Middle: Stock, Order Priority, Locked Status */}
                          <div className="flex items-center justify-between gap-2 flex-wrap bg-[#F7F7F8] p-2.5 rounded-xl border border-zinc-100">
                            <div className="flex items-center gap-2">
                              <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                game.in_stock !== false 
                                  ? "bg-green-50 text-[#16A34A] border border-green-200/60" 
                                  : "bg-red-50 text-[#DC2626] border border-red-200/60"
                              }`}>
                                {game.in_stock !== false ? "IN STOCK" : "OUT OF STOCK"}
                              </span>
                              <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500">
                                ORDER: {game.display_order ?? 0}
                              </span>
                            </div>
                            {isPlatformLocked && (
                              <span className="px-1.5 py-0.5 rounded bg-red-50 text-[#E10600] border border-red-100 font-black text-[8px] uppercase tracking-wider">
                                🔒 LOCKED
                              </span>
                            )}
                          </div>
                          
                          {/* Card Bottom: Pricing & Actions */}
                          <div className="flex items-center justify-between pt-1 border-t border-zinc-100/80 gap-2">
                            <div className="flex items-baseline gap-1.5 shrink-0">
                              <span className="font-black text-zinc-950 text-base">₹{game.price}</span>
                              {game.steam_price && (
                                <span className="text-zinc-400 line-through text-xs font-semibold">₹{game.steam_price}</span>
                              )}
                              {disc > 0 && (
                                <span className="text-[#E00000] font-bold text-[10px]">-{disc}%</span>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-1.5 shrink-0">
                              <button 
                                onClick={() => window.open(`/game/${game.id}`, '_blank')}
                                className="w-8 h-8 flex items-center justify-center hover:bg-zinc-100 text-zinc-700 rounded-lg border border-zinc-200 bg-white active:scale-95 transition cursor-pointer"
                                title="View Product"
                                aria-label="View game"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => handleEditGame(game)}
                                className="w-8 h-8 flex items-center justify-center hover:bg-zinc-100 text-zinc-700 rounded-lg border border-zinc-200 bg-white active:scale-95 transition cursor-pointer"
                                title="Edit parameters"
                                aria-label="Edit game"
                              >
                                <PencilSimple className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => handleDeleteGame(game.id)}
                                className="w-8 h-8 flex items-center justify-center hover:bg-red-50 text-red-500 hover:text-red-700 rounded-lg border border-red-100 bg-red-50/30 active:scale-95 transition cursor-pointer"
                                title="Delete product"
                                aria-label="Delete game"
                              >
                                <Trash className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 4. CUSTOMERS */}
          {activeTab === "users" && (
            <div className="space-y-6 animate-fade-in text-left">
              <div>
                <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Customer Accounts</h1>
                <p className="text-xs text-zinc-500 mt-1">Review user accounts, sign-up records, and order transaction volumes.</p>
              </div>

              {loadingUsers ? (
                <div className="py-20 flex justify-center items-center bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">
                  <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                </div>
              ) : usersList.length === 0 ? (
                <div className="py-20 text-center text-zinc-400 text-xs bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">No customer profiles registered.</div>
              ) : (
                <>
                  {/* Desktop/Tablet Table */}
                  <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-[#E5E5E5] text-left text-xs">
                        <thead>
                          <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                            <th className="py-3.5 px-6">Name</th>
                            <th className="py-3.5 px-6">Email</th>
                            <th className="py-3.5 px-6">Total Orders</th>
                            <th className="py-3.5 px-6">Total Spent</th>
                            <th className="py-3.5 px-6">Joined Date</th>
                            <th className="py-3.5 px-6">System Role</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {usersList.map(cust => (
                            <tr key={cust.id} className="hover:bg-zinc-50/50 transition duration-150">
                              <td className="py-4 px-6 font-bold text-zinc-800">{cust.full_name || "Verified Customer"}</td>
                              <td className="py-4 px-6 text-zinc-600 font-medium">{cust.email}</td>
                              <td className="py-4 px-6 font-semibold text-zinc-700">{cust.orders_count || 0}</td>
                              <td className="py-4 px-6 font-black text-zinc-900 text-sm">₹{cust.total_spent?.toLocaleString() || 0}</td>
                              <td className="py-4 px-6 text-zinc-500 font-medium">
                                {cust.created_at ? new Date(cust.created_at).toLocaleDateString() : "Not specified"}
                              </td>
                              <td className="py-4 px-6">
                                <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                  cust.role === "admin" 
                                    ? "bg-red-50 text-red-600 border border-red-100" 
                                    : "bg-blue-50 text-blue-600 border border-blue-100"
                                }`}>
                                  {cust.role || "user"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards Layout */}
                  <div className="flex flex-col gap-3.5 md:hidden">
                    {usersList.map(cust => (
                      <div key={cust.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs text-xs text-left space-y-3.5">
                        <div className="flex justify-between items-start">
                          <div className="min-w-0 pr-2">
                            <h4 className="font-bold text-zinc-950 text-sm truncate">{cust.full_name || "Verified Customer"}</h4>
                            <p className="text-zinc-500 font-medium text-[11px] mt-0.5 truncate">{cust.email}</p>
                          </div>
                          <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider shrink-0 ${
                            cust.role === "admin" 
                              ? "bg-red-50 text-red-600 border border-red-100" 
                              : "bg-blue-50 text-blue-600 border border-blue-100"
                          }`}>
                            {cust.role || "user"}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 bg-zinc-50 border border-zinc-100 rounded-xl p-3">
                          <div>
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">ORDERS</span>
                            <span className="font-black text-zinc-950 text-sm">{cust.orders_count || 0}</span>
                          </div>
                          <div>
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">TOTAL SPENT</span>
                            <span className="font-black text-zinc-950 text-sm">₹{cust.total_spent?.toLocaleString() || 0}</span>
                          </div>
                          <div className="col-span-2 border-t border-zinc-200/60 pt-2">
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">JOINED DATE</span>
                            <span className="font-bold text-zinc-800">{cust.created_at ? new Date(cust.created_at).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' }) : "Not specified"}</span>
                          </div>
                        </div>

                        <div className="border-t border-zinc-100 pt-3 flex justify-end">
                          <button 
                            onClick={() => {
                              setOrderSearch(cust.email);
                              setOrderStatusFilter("ALL");
                              setActiveTab("orders");
                            }}
                            className="px-3.5 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-bold uppercase text-[10px] tracking-wider rounded-lg transition cursor-pointer"
                          >
                            View Orders →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* 5. CATEGORIES */}
          {activeTab === "categories" && (
            <div className="space-y-6 animate-fade-in text-left">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none">
                <div>
                  <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Categories Management</h1>
                  <p className="text-xs text-zinc-500 mt-1">Manage game genres and categories dynamically linked to the database.</p>
                </div>
                <button
                  onClick={() => { setEditingCategory(null); setCategoryName(""); setShowCategoryModal(true); }}
                  className="bg-[#E10600] hover:bg-[#C80500] text-white px-5 py-2.5 rounded-lg text-xs font-black uppercase tracking-wider transition-transform hover:scale-102 flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <Plus className="w-4 h-4" /> Add Category
                </button>
              </div>

              <div>
                {loadingCategories ? (
                  <div className="py-20 flex justify-center items-center bg-white border border-[#E5E5E5] rounded-[16px] shadow-xs">
                    <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                  </div>
                ) : categories.length === 0 ? (
                  <div className="py-20 text-center select-none text-zinc-400 text-xs bg-white border border-[#E5E5E5] rounded-[16px]">No product categories created yet.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {categories.map(cat => {
                      const gameCount = games.filter(g => g.category_id === cat.id).length;
                      return (
                        <div key={cat.id} className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 shadow-xs flex flex-col justify-between select-none text-left">
                          <div>
                            <div className="flex items-center gap-2 text-zinc-800 font-black text-sm uppercase tracking-wide">
                              <span>🎮</span>
                              <span>{cat.name}</span>
                            </div>
                            <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider block mt-1.5">{gameCount} Games Listed</span>
                            <span className="font-mono text-[9px] text-zinc-400 block mt-0.5 truncate" title={cat.id}>ID: #{cat.id.substring(0, 8)}</span>
                          </div>
                          
                          <div className="flex items-center gap-1.5 justify-end mt-5 pt-3 border-t border-zinc-100">
                            <button 
                              onClick={() => handleEditCategory(cat)}
                              className="px-3 py-1.5 hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 rounded-lg transition border border-zinc-200 bg-zinc-50 text-[10px] font-black uppercase tracking-wider cursor-pointer"
                              title="Rename category"
                            >
                              EDIT
                            </button>
                            <button 
                              onClick={() => handleDeleteCategory(cat.id)}
                              className="px-3 py-1.5 hover:bg-red-50 text-red-500 hover:text-red-700 rounded-lg transition border border-red-100 bg-red-50/20 text-[10px] font-black uppercase tracking-wider cursor-pointer"
                              title="Delete category"
                            >
                              DELETE
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 6. PLATFORMS */}
          {activeTab === "platforms" && (
            <div className="space-y-6 animate-fade-in text-left">
              {/* Header & Metrics Summary */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none">
                <div>
                  <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Platform Configurations</h1>
                  <p className="text-xs text-zinc-500 mt-1">Manage database-driven platform locking, storefront visibility, and real catalog counts.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={fetchPlatforms}
                    disabled={loadingPlatforms}
                    className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E5E5E5] hover:bg-zinc-50 rounded-xl text-xs font-bold text-zinc-700 transition"
                  >
                    <ArrowClockwise className={`w-3.5 h-3.5 ${loadingPlatforms ? "animate-spin text-[#E10600]" : ""}`} />
                    Refresh
                  </button>
                </div>
              </div>

              {/* Summary Metrics Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 select-none">
                <div className="bg-white border border-[#E5E5E5] rounded-xl p-3.5">
                  <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Total Platforms</span>
                  <span className="text-xl font-black text-zinc-900">{platforms.length}</span>
                </div>
                <div className="bg-white border border-[#E5E5E5] rounded-xl p-3.5">
                  <span className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider block">Unlocked / Live</span>
                  <span className="text-xl font-black text-emerald-600">
                    {platforms.filter(p => !p.is_locked).length}
                  </span>
                </div>
                <div className="bg-white border border-[#E5E5E5] rounded-xl p-3.5">
                  <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider block">Locked / Hidden</span>
                  <span className="text-xl font-black text-zinc-700">
                    {platforms.filter(p => p.is_locked).length}
                  </span>
                </div>
                <div className="bg-white border border-[#E5E5E5] rounded-xl p-3.5">
                  <span className="text-[10px] text-[#E10600] font-bold uppercase tracking-wider block">Total Linked Products</span>
                  <span className="text-xl font-black text-[#E10600]">
                    {platforms.reduce((acc, p) => acc + (p.product_count || 0), 0)}
                  </span>
                </div>
              </div>

              {/* Desktop / Tablet Table Layout */}
              <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                <table className="min-w-full divide-y divide-[#E5E5E5] text-left text-xs">
                  <thead>
                    <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                      <th className="py-3.5 px-6">Platform</th>
                      <th className="py-3.5 px-6">Account Type</th>
                      <th className="py-3.5 px-6">Product Count</th>
                      <th className="py-3.5 px-6">System Status</th>
                      <th className="py-3.5 px-6">Storefront Visibility</th>
                      <th className="py-3.5 px-6 text-right">Lock / Unlock Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {platforms.map((plat) => {
                      const isLive = !plat.is_locked;
                      const isBusy = lockingPlatformId === plat.id;
                      return (
                        <tr key={plat.id} className="hover:bg-zinc-50/80 transition-colors">
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-zinc-50 border border-zinc-100 shrink-0">
                                <PlatformIcon platform={plat.name} size={18} className="text-zinc-700" />
                              </div>
                              <div>
                                <span className="font-bold text-zinc-900 block">{plat.name}</span>
                                <span className="text-[10px] text-zinc-400 font-medium">{plat.launcher_name || plat.name}</span>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className="font-semibold text-zinc-700">{plat.account_type_label || getPlatformAccountType(plat.name)}</span>
                          </td>
                          <td className="py-4 px-6">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-800 font-extrabold text-[11px]">
                              {plat.product_count || 0} Products
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`text-[9px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                              plat.is_active !== false
                                ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                                : "bg-zinc-100 text-zinc-500 border border-zinc-200"
                            }`}>
                              {plat.is_active !== false ? "ACTIVE" : "INACTIVE"}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`text-[9px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 ${
                              isLive
                                ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                                : "bg-red-50 text-[#E10600] border border-red-100"
                            }`}>
                              {isLive ? <LockOpen className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                              {isLive ? "UNLOCKED" : "LOCKED"}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleTogglePlatformLock(plat)}
                                disabled={isBusy}
                                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition ${
                                  isLive
                                    ? "bg-red-50 hover:bg-red-100 text-[#E10600] border border-red-200"
                                    : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
                                } disabled:opacity-50 min-h-[36px]`}
                              >
                                {isBusy ? (
                                  <ArrowClockwise className="w-3.5 h-3.5 animate-spin" />
                                ) : isLive ? (
                                  <Lock className="w-3.5 h-3.5" weight="bold" />
                                ) : (
                                  <LockOpen className="w-3.5 h-3.5" weight="bold" />
                                )}
                                {isLive ? "LOCK" : "UNLOCK"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards Layout (No Horizontal Overflow) */}
              <div className="md:hidden space-y-3">
                {platforms.map((plat) => {
                  const isLive = !plat.is_locked;
                  const isBusy = lockingPlatformId === plat.id;
                  return (
                    <div key={plat.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs flex flex-col gap-3 select-none">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-zinc-50 border border-zinc-100 shrink-0">
                            <PlatformIcon platform={plat.name} size={20} className="text-zinc-700" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-zinc-900 text-sm truncate">{plat.name}</h3>
                            <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">
                              {plat.product_count || 0} {plat.product_count === 1 ? "Product" : "Products"}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                            isLive
                              ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                              : "bg-red-50 text-[#E10600] border border-red-100"
                          }`}>
                            {isLive ? <LockOpen className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
                            {isLive ? "UNLOCKED" : "LOCKED"}
                          </span>
                        </div>
                      </div>

                      <div className="bg-zinc-50 border border-zinc-100 rounded-xl p-2.5 flex items-center justify-between text-xs">
                        <span className="text-zinc-500 font-medium text-[11px]">Account Type:</span>
                        <span className="font-bold text-zinc-800 text-[11px]">{plat.account_type_label || getPlatformAccountType(plat.name)}</span>
                      </div>

                      <button
                        onClick={() => handleTogglePlatformLock(plat)}
                        disabled={isBusy}
                        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition ${
                          isLive
                            ? "bg-red-50 hover:bg-red-100 text-[#E10600] border border-red-200"
                            : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
                        } disabled:opacity-50 min-h-[42px]`}
                      >
                        {isBusy ? (
                          <ArrowClockwise className="w-3.5 h-3.5 animate-spin" />
                        ) : isLive ? (
                          <Lock className="w-3.5 h-3.5" weight="bold" />
                        ) : (
                          <LockOpen className="w-3.5 h-3.5" weight="bold" />
                        )}
                        {isLive ? "LOCK STOREFRONT ACCESS" : "UNLOCK STOREFRONT ACCESS"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 7. OFFERS & DEALS */}
          {activeTab === "offers" && (
            <div className="space-y-6 animate-fade-in text-left">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none">
                <div>
                  <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Active Promotional Deals</h1>
                  <p className="text-xs text-zinc-500 mt-1">Catalog titles currently featured with active discount pricing.</p>
                </div>
                <div className="bg-white border border-[#E5E5E5] px-4 py-2 rounded-lg text-xs font-bold text-zinc-700">
                  Total Active Deals: <span className="text-[#E10600] font-black">{dealsGames.length}</span>
                </div>
              </div>

              {dealsGames.length === 0 ? (
                <div className="py-20 text-center text-zinc-400 text-xs bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">No active discounted games.</div>
              ) : (
                <>
                  {/* Desktop/Tablet Table */}
                  <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-[#E5E5E5] text-left text-xs">
                        <thead>
                          <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                            <th className="py-3.5 px-6">Image</th>
                            <th className="py-3.5 px-6">Game Title</th>
                            <th className="py-3.5 px-6">Platform</th>
                            <th className="py-3.5 px-6">Our Price</th>
                            <th className="py-3.5 px-6">Steam Price</th>
                            <th className="py-3.5 px-6">Discount</th>
                            <th className="py-3.5 px-6 text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {dealsGames.map(game => {
                            const disc = Math.round(((game.steam_price - game.price) / game.steam_price) * 100);
                            return (
                              <tr key={game.id} className="hover:bg-zinc-50/50 transition duration-150">
                                <td className="py-4 px-6 shrink-0">
                                  <img src={game.image_url} alt="" className="w-12 h-8.5 object-cover rounded-lg bg-zinc-100 border border-zinc-200/50" />
                                </td>
                                <td className="py-4 px-6 font-bold text-zinc-800 text-sm">{game.title}</td>
                                <td className="py-4 px-6 text-zinc-500 font-bold uppercase text-[10px]">{game.platform || "Steam"}</td>
                                <td className="py-4 px-6 font-black text-zinc-900 text-sm">₹{game.price}</td>
                                <td className="py-4 px-6 text-zinc-400 line-through font-semibold">₹{game.steam_price}</td>
                                <td className="py-4 px-6">
                                  <span className="bg-red-50 text-[#E10600] border border-red-100 px-2.5 py-0.5 rounded-full text-[10px] font-black">
                                    -{disc}% OFF
                                  </span>
                                </td>
                                <td className="py-4 px-6 text-center">
                                  <button
                                    onClick={() => handleEditGame(game)}
                                    className="p-2 hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 rounded-lg transition"
                                    title="Edit Deal Price"
                                  >
                                    <PencilSimple className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Grid/Cards Layout */}
                  <div className="flex flex-col md:hidden gap-3.5">
                    {dealsGames.map(game => {
                      const disc = Math.round(((game.steam_price - game.price) / game.steam_price) * 100);
                      return (
                        <div key={game.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs text-xs text-left space-y-3.5">
                          <div className="flex gap-3.5">
                            <img src={game.image_url} alt="" className="w-20 h-14 object-cover rounded-xl bg-zinc-100 border border-zinc-200/50 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-zinc-950 text-sm truncate">{game.title}</h4>
                              <span className="text-zinc-500 font-bold uppercase text-[9px] block mt-0.5">{game.platform || "Steam"}</span>
                              <span className="inline-block mt-1 bg-red-50 text-[#E10600] border border-red-100 px-2 py-0.5 rounded-full text-[9px] font-black">
                                {disc}% OFF
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 bg-zinc-50 border border-zinc-100 rounded-xl p-3">
                            <div>
                              <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">OUR PRICE</span>
                              <span className="font-black text-zinc-950 text-sm">₹{game.price}</span>
                            </div>
                            <div>
                              <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">STEAM PRICE</span>
                              <span className="text-zinc-400 line-through font-semibold text-xs">₹{game.steam_price}</span>
                            </div>
                          </div>

                          <div className="flex justify-end border-t border-zinc-100 pt-3">
                            <button
                              onClick={() => handleEditGame(game)}
                              className="px-4 py-2 bg-[#E10600] hover:bg-[#C80500] text-white rounded-lg text-[10px] font-bold uppercase tracking-wider transition cursor-pointer flex items-center gap-1.5 shadow-2xs"
                              title="Edit Deal Price"
                            >
                              <PencilSimple className="w-3.5 h-3.5" /> Edit Deal
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* 8. PAYMENTS */}
          {activeTab === "payments" && (
            <div className="space-y-6 animate-fade-in text-left">
              <div>
                <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Payment Registries</h1>
                <p className="text-xs text-zinc-500 mt-1">Audit customer UTR submission transaction details.</p>
              </div>

              {loadingOrders ? (
                <div className="py-20 flex justify-center items-center bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">
                  <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                </div>
              ) : verifiedPayments.length === 0 ? (
                <div className="py-20 text-center text-zinc-400 text-xs bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">No transaction records found.</div>
              ) : (
                <>
                  {/* Desktop/Tablet Table */}
                  <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-[#E5E5E5] text-left text-xs">
                        <thead>
                          <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                            <th className="py-3.5 px-6">Payment ID</th>
                            <th className="py-3.5 px-6">UTR / Reference No</th>
                            <th className="py-3.5 px-6">Customer</th>
                            <th className="py-3.5 px-6">Amount</th>
                            <th className="py-3.5 px-6">Method</th>
                            <th className="py-3.5 px-6">Verified status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {verifiedPayments.map(payment => (
                            <tr key={payment.id} className="hover:bg-zinc-50/50 transition duration-150">
                              <td className="py-4 px-6 font-mono text-[10px] text-zinc-500 font-bold">#{payment.id.slice(0, 8)}</td>
                              <td className="py-4 px-6 font-mono text-zinc-800 font-bold">
                                <div className="flex items-center gap-1.5">
                                  <span>{payment.transaction_id || "--"}</span>
                                  {payment.transaction_id && (
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(payment.transaction_id);
                                        toast.success("UTR copied to clipboard!");
                                      }}
                                      className="p-1 hover:bg-zinc-100 rounded text-zinc-500 hover:text-zinc-800 transition"
                                      title="Copy UTR"
                                    >
                                      <Copy className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="py-4 px-6">
                                <p className="font-bold text-zinc-800">{payment.billing_name || payment.profiles?.full_name}</p>
                              </td>
                              <td className="py-4 px-6 font-black text-zinc-900 text-sm">₹{payment.total_price}</td>
                              <td className="py-4 px-6 text-zinc-500 uppercase font-black text-[9px] tracking-wider">UPI / QR</td>
                              <td className="py-4 px-6">
                                <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                  payment.payment_status === "paid" 
                                    ? "bg-green-50 text-[#16A34A] border border-green-100" 
                                    : payment.payment_status === "failed" 
                                    ? "bg-red-50 text-[#DC2626] border border-red-100" 
                                    : "bg-zinc-100 text-zinc-600 border border-zinc-200"
                                }`}>
                                  {payment.payment_status || "pending"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards Layout */}
                  <div className="flex flex-col md:hidden gap-3.5">
                    {verifiedPayments.map(payment => {
                      const customerName = payment.billing_name || payment.profiles?.full_name || "Verified Customer";
                      const statusLabel = (payment.payment_status || "PENDING").toUpperCase();
                      return (
                        <div key={payment.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs text-xs text-left space-y-3.5">
                          <div className="flex justify-between items-center border-b border-zinc-100 pb-2.5">
                            <span className="font-mono text-[10px] text-zinc-500 font-bold">#{payment.id.substring(0, 8).toUpperCase()}</span>
                            <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                              payment.payment_status === "paid" 
                                ? "bg-green-50 text-[#16A34A] border border-green-100" 
                                : payment.payment_status === "failed" || payment.payment_status === "rejected"
                                ? "bg-red-50 text-[#DC2626] border border-red-100" 
                                : "bg-amber-50 text-amber-600 border border-amber-100"
                            }`}>
                              {statusLabel}
                            </span>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">CUSTOMER</span>
                                <span className="font-bold text-zinc-950 block">{customerName}</span>
                              </div>
                              <div>
                                <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">AMOUNT</span>
                                <span className="font-black text-zinc-950 text-sm">₹{payment.total_price}</span>
                              </div>
                            </div>
                            
                            <div>
                              <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">UTR / REFERENCE NO</span>
                              <div className="flex items-center gap-1.5 font-mono select-all bg-zinc-50 px-3 py-2 rounded-xl border border-zinc-200 break-all text-left justify-between">
                                <span className="font-bold text-zinc-800 text-[11px]">{payment.transaction_id || "--"}</span>
                                {payment.transaction_id && (
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(payment.transaction_id);
                                      toast.success("UTR copied to clipboard!");
                                    }}
                                    className="p-1 hover:bg-zinc-200 rounded text-zinc-550 hover:text-zinc-800 shrink-0 transition"
                                    title="Copy UTR"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            <div className="border-t border-zinc-100 pt-3 flex items-center justify-between">
                              <div>
                                <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">METHOD</span>
                                <span className="text-zinc-500 uppercase font-black text-[9px] tracking-wider">UPI / QR</span>
                              </div>
                              <span className="text-[10px] text-zinc-400 font-medium">
                                {payment.created_at ? new Date(payment.created_at).toLocaleDateString("en-IN", { day: 'numeric', month: 'short' }) : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* 9. ANALYTICS */}
          {activeTab === "analytics" && (
            <div className="space-y-6 animate-fade-in text-left">
              <div>
                <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Store Analytics</h1>
                <p className="text-xs text-zinc-500 mt-1">Review sales stats, conversion levels, and transaction charts.</p>
              </div>

              {loadingOrders ? (
                <div className="py-20 flex justify-center items-center bg-white border border-[#E5E5E5] rounded-[16px] shadow-xs">
                  <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-8 text-center max-w-xl mx-auto shadow-xs select-none">
                  <ChartLineUp className="w-7 h-7 text-zinc-400 mx-auto mb-4" />
                  <p className="text-zinc-500 text-xs">No analytics data available right now.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 shadow-xs select-none">
                      <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block mb-1">Lifetime Revenue</span>
                      <h3 className="text-2xl font-black text-zinc-950">₹{stats.revenue.toLocaleString()}</h3>
                      <span className="text-[10px] text-emerald-600 font-bold mt-1.5 block">Verified Orders</span>
                    </div>

                    <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 shadow-xs select-none">
                      <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block mb-1">Total Orders</span>
                      <h3 className="text-2xl font-black text-zinc-950">{orders.length}</h3>
                      <span className="text-[10px] text-blue-600 font-bold mt-1.5 block">{calculatedStats.processingCount} In Processing</span>
                    </div>

                    <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-5 shadow-xs select-none">
                      <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block mb-1">Average Order Value</span>
                      <h3 className="text-2xl font-black text-zinc-950">
                        ₹{orders.length ? Math.round(stats.revenue / orders.length).toLocaleString() : 0}
                      </h3>
                      <span className="text-[10px] text-zinc-500 font-bold mt-1.5 block">Per Transaction</span>
                    </div>
                  </div>

                  {/* Best Selling Games List */}
                  <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-6 shadow-xs select-none">
                    <h3 className="text-xs font-black uppercase tracking-wider text-zinc-800 mb-4 border-b border-zinc-100 pb-2">
                      Best-Selling Games in Storefront
                    </h3>
                    {bestSellingGames.length === 0 ? (
                      <p className="text-xs text-zinc-400 py-4">No sales recorded yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {bestSellingGames.map((item, idx) => (
                          <div key={idx} className="bg-zinc-50 border border-zinc-200/60 rounded-[16px] p-3.5 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-red-100 text-[#E10600] font-black text-xs flex items-center justify-center shrink-0">
                              #{idx + 1}
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-bold text-zinc-900 text-xs truncate">{item.title}</h4>
                              <span className="text-[10px] text-zinc-500 font-semibold mt-0.5 block">
                                {item.count} sold • ₹{item.revenue}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 10. SUPPORT */}
          {activeTab === "support" && (
            <div className="space-y-6 animate-fade-in text-left">
              <div>
                <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Customer Support Inquiries</h1>
                <p className="text-xs text-zinc-500 mt-1">Review contact inquiries and connect directly with customers.</p>
              </div>

              {loadingContacts ? (
                <div className="py-20 flex justify-center items-center bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">
                  <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                </div>
              ) : contacts.length === 0 ? (
                <div className="py-20 text-center select-none text-zinc-400 text-xs bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">No support inquiries filed.</div>
              ) : (
                <>
                  {/* Desktop/Tablet Table */}
                  <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-[#E5E5E5] text-left text-xs">
                        <thead>
                          <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                            <th className="py-3.5 px-6">Name</th>
                            <th className="py-3.5 px-6">Email Address</th>
                            <th className="py-3.5 px-6">Message Content</th>
                            <th className="py-3.5 px-6">Date</th>
                            <th className="py-3.5 px-6 text-center">Contact</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {contacts.map(msg => (
                            <tr key={msg.id} className="hover:bg-zinc-50/50 transition duration-150">
                              <td className="py-4 px-6 font-bold text-zinc-800">{msg.name}</td>
                              <td className="py-4 px-6 text-zinc-600 font-medium">{msg.email}</td>
                              <td className="py-4 px-6 text-zinc-700 max-w-xs font-medium whitespace-pre-wrap break-words" title={msg.message}>
                                {msg.message}
                              </td>
                              <td className="py-4 px-6 text-zinc-400 font-medium">
                                {new Date(msg.created_at).toLocaleString()}
                              </td>
                              <td className="py-4 px-6 text-center">
                                <a
                                  href={`mailto:${msg.email}?subject=CG39 Support Response`}
                                  className="px-3 py-1 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-md font-bold text-[10px] uppercase tracking-wider inline-block"
                                >
                                  Reply Email
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards Layout */}
                  <div className="flex flex-col md:hidden gap-3.5">
                    {contacts.map(msg => {
                      const dateStr = new Date(msg.created_at).toLocaleDateString("en-IN", {
                        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      });
                      return (
                        <div key={msg.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs text-xs text-left space-y-3.5">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">CUSTOMER NAME</span>
                              <h4 className="font-bold text-zinc-950 text-sm">{msg.name}</h4>
                            </div>
                            <span className="text-[10px] text-zinc-500 font-bold bg-zinc-50 border border-zinc-200 px-2 py-0.5 rounded-full shrink-0">
                              {timeAgo(msg.created_at)}
                            </span>
                          </div>
                          
                          <div>
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">EMAIL</span>
                            <span className="font-bold text-zinc-800 break-all block">{msg.email}</span>
                          </div>

                          <div className="bg-zinc-50 border border-zinc-200/70 rounded-xl p-3">
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">MESSAGE</span>
                            <p className="text-zinc-700 leading-relaxed break-words font-medium whitespace-pre-wrap">
                              {msg.message}
                            </p>
                          </div>
                          
                          <div className="flex items-center justify-between border-t border-zinc-100 pt-3">
                            <div>
                              <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">DATE</span>
                              <span className="font-bold text-zinc-800">{dateStr}</span>
                            </div>
                            <a
                              href={`mailto:${msg.email}?subject=CG39 Support Response`}
                              className="px-3.5 py-1.5 bg-[#E10600] hover:bg-[#C80500] text-white rounded-lg font-bold text-[10px] uppercase tracking-wider transition shrink-0 cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                            >
                              Reply Email
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* 10.5 REVIEWS MANAGEMENT */}
          {activeTab === "reviews" && (
            <div className="space-y-6 animate-fade-in text-left">
              <div className="flex justify-between items-center select-none">
                <div>
                  <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Customer Reviews</h1>
                  <p className="text-xs text-zinc-500 mt-1">Approve, reject, and moderate customer reviews for your products.</p>
                </div>
                <button
                  onClick={fetchAdminReviews}
                  className="px-3 py-1.5 bg-[#E10600] hover:bg-[#C80500] text-white rounded-lg font-bold text-xs uppercase tracking-wider transition flex items-center gap-1.5 cursor-pointer"
                  disabled={loadingReviews}
                >
                  <ArrowClockwise className={`w-3.5 h-3.5 ${loadingReviews ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>

              {loadingReviews ? (
                <div className="py-20 flex justify-center items-center bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">
                  <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                </div>
              ) : reviews.length === 0 ? (
                <div className="py-20 text-center select-none text-zinc-400 text-xs font-semibold bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">
                  No customer reviews found.
                </div>
              ) : (
                <>
                  {/* Desktop/Tablet Table */}
                  <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-[#E5E5E5] text-xs text-left">
                        <thead>
                          <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                            <th className="py-3.5 px-6">Customer</th>
                            <th className="py-3.5 px-6">Game / Product</th>
                            <th className="py-3.5 px-6 text-center">Rating</th>
                            <th className="py-3.5 px-6">Review Comment</th>
                            <th className="py-3.5 px-6">Purchase Info</th>
                            <th className="py-3.5 px-6">Date</th>
                            <th className="py-3.5 px-6 text-center">Status</th>
                            <th className="py-3.5 px-6 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {reviews.map(rev => {
                            const customerName = rev.profiles?.full_name || "Verified Customer";
                            const customerEmail = rev.profiles?.email || "";
                            const gameTitle = rev.games?.title || "Unknown Game";
                            const ratingStars = rev.rating;
                            const purchaseOrder = rev.purchase_info;
                            const dateStr = new Date(rev.created_at).toLocaleDateString(undefined, {
                              year: 'numeric', month: 'short', day: 'numeric'
                            });

                            return (
                              <tr key={rev.id} className="hover:bg-zinc-50/50 transition duration-150">
                                {/* Customer info */}
                                <td className="py-4 px-6">
                                  <div className="font-bold text-zinc-800">{customerName}</div>
                                  {customerEmail && <div className="text-[10px] text-zinc-500 font-medium mt-0.5">{customerEmail}</div>}
                                </td>

                                {/* Game info */}
                                <td className="py-4 px-6 font-bold text-zinc-800 max-w-[160px] truncate" title={gameTitle}>
                                  {gameTitle}
                                </td>

                                {/* Rating */}
                                <td className="py-4 px-6 text-center">
                                  <div className="flex gap-0.5 justify-center">
                                    {[1, 2, 3, 4, 5].map(star => (
                                      <Star
                                        key={star}
                                        weight="fill"
                                        className={`w-3.5 h-3.5 ${star <= ratingStars ? "text-yellow-500" : "text-zinc-200"}`}
                                      />
                                    ))}
                                  </div>
                                </td>

                                {/* Review Comment */}
                                <td className="py-4 px-6 text-zinc-700 max-w-xs font-medium whitespace-pre-wrap break-words" title={rev.comment}>
                                  {rev.comment}
                                </td>

                                {/* Purchase Info */}
                                <td className="py-4 px-6 font-medium">
                                  {purchaseOrder ? (
                                    <div className="space-y-0.5">
                                      <span className="font-mono text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-100 uppercase font-bold">
                                        #{purchaseOrder.order_id.substring(0, 8).toUpperCase()}
                                      </span>
                                      <div className="text-[9px] text-zinc-500 font-bold uppercase tracking-wider mt-0.5">
                                        {purchaseOrder.status}
                                      </div>
                                    </div>
                                  ) : rev.is_verified ? (
                                    <span className="text-[9px] bg-green-50 text-emerald-600 px-1.5 py-0.5 rounded font-bold uppercase">
                                      Verified
                                    </span>
                                  ) : (
                                    <span className="text-[9px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded font-bold uppercase">
                                      Not Verified
                                    </span>
                                  )}
                                </td>

                                {/* Date */}
                                <td className="py-4 px-6 text-zinc-400 font-medium">
                                  {dateStr}
                                </td>

                                {/* Status */}
                                <td className="py-4 px-6 text-center">
                                  {rev.status === "approved" ? (
                                    <span className="px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-green-50 text-[#16A34A] border border-green-100">Approved</span>
                                  ) : rev.status === "rejected" ? (
                                    <span className="px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-red-50 text-red-600 border border-red-100">Rejected</span>
                                  ) : (
                                    <span className="px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-600 border border-amber-100">Pending</span>
                                  )}
                                </td>

                                {/* Actions */}
                                <td className="py-4 px-6 text-right">
                                  <div className="flex gap-2 justify-end">
                                    {rev.status !== "approved" && (
                                      <button
                                        onClick={() => handleReviewStatusUpdate(rev.id, "approved")}
                                        className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold text-[10px] uppercase tracking-wider transition select-none cursor-pointer"
                                      >
                                        Approve
                                      </button>
                                    )}
                                    {rev.status !== "rejected" && (
                                      <button
                                        onClick={() => handleReviewStatusUpdate(rev.id, "rejected")}
                                        className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded font-bold text-[10px] uppercase tracking-wider transition select-none cursor-pointer"
                                      >
                                        Reject
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards Layout */}
                  <div className="flex flex-col md:hidden gap-3.5">
                    {reviews.map(rev => {
                      const customerName = rev.profiles?.full_name || "Verified Customer";
                      const customerEmail = rev.profiles?.email || "";
                      const gameTitle = rev.games?.title || "Unknown Game";
                      const ratingStars = rev.rating;
                      const purchaseOrder = rev.purchase_info;
                      const dateStr = new Date(rev.created_at).toLocaleDateString("en-IN", {
                        year: 'numeric', month: 'short', day: 'numeric'
                      });

                      return (
                        <div key={rev.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs text-xs text-left space-y-3.5">
                          <div className="flex justify-between items-start border-b border-zinc-100 pb-2.5">
                            <div>
                              <span className="font-bold text-zinc-950 block">{customerName}</span>
                              {customerEmail && <span className="text-[10px] text-zinc-400 block font-medium mt-0.5">{customerEmail}</span>}
                            </div>
                            <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                              rev.status === "approved" 
                                ? "bg-green-50 text-green-600 border border-green-100" 
                                : rev.status === "rejected"
                                ? "bg-red-50 text-red-600 border border-red-100" 
                                : "bg-amber-50 text-amber-600 border border-amber-100"
                            }`}>
                              {rev.status?.toUpperCase() || "PENDING"}
                            </span>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">GAME</span>
                                <span className="font-bold text-zinc-950 block truncate" title={gameTitle}>{gameTitle}</span>
                              </div>
                              <div>
                                <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">DATE</span>
                                <span className="font-bold text-zinc-950 block">{dateStr}</span>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 bg-zinc-50 border border-zinc-100 rounded-xl p-3">
                              <div>
                                <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">RATING</span>
                                <div className="flex gap-0.5 mt-1">
                                  {[1, 2, 3, 4, 5].map(star => (
                                    <Star
                                      key={star}
                                      weight="fill"
                                      className={`w-3.5 h-3.5 ${star <= ratingStars ? "text-yellow-500" : "text-zinc-200"}`}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div>
                                <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">PURCHASE INFO</span>
                                {purchaseOrder ? (
                                  <span className="font-mono text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-100 uppercase font-bold inline-block mt-0.5">
                                    #{purchaseOrder.order_id.substring(0, 8).toUpperCase()}
                                  </span>
                                ) : rev.is_verified ? (
                                  <span className="text-[9px] bg-green-50 text-emerald-600 px-1.5 py-0.5 rounded font-bold uppercase inline-block mt-0.5">
                                    Verified
                                  </span>
                                ) : (
                                  <span className="text-[9px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded font-bold uppercase inline-block mt-0.5">
                                    Not Verified
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="bg-zinc-50 border border-zinc-150 rounded-xl p-3">
                              <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">COMMENT</span>
                              <p className="text-zinc-700 leading-relaxed font-medium break-words whitespace-pre-wrap">"{rev.comment}"</p>
                            </div>

                            <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3">
                              {rev.status !== "approved" && (
                                <button
                                  onClick={() => handleReviewStatusUpdate(rev.id, "approved")}
                                  className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-[10px] uppercase tracking-wider transition select-none cursor-pointer"
                                >
                                  Approve
                                </button>
                              )}
                              {rev.status !== "rejected" && (
                                <button
                                  onClick={() => handleReviewStatusUpdate(rev.id, "rejected")}
                                  className="px-3.5 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold text-[10px] uppercase tracking-wider transition select-none cursor-pointer"
                                >
                                  Reject
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* 11. SETTINGS */}
          {activeTab === "settings" && (
            <div className="space-y-6 animate-fade-in text-left select-none">
              <div>
                <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">System Settings</h1>
                <p className="text-xs text-zinc-500 mt-1">Manage system parameters, admin options, and security protocols.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Store Profile settings */}
                <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-6 shadow-xs">
                  <h3 className="text-xs font-black uppercase tracking-wider text-zinc-800 mb-4 border-b border-zinc-100 pb-2 flex items-center gap-1.5"><Gear className="w-4 h-4" /> Store Configurations</h3>
                  <div className="space-y-4 text-xs">
                    <div>
                      <label className="text-zinc-400 uppercase font-bold tracking-wider text-[9px] block mb-1.5">Store Name</label>
                      <input 
                        type="text" 
                        value={storeSettings.store_name} 
                        onChange={(e) => setStoreSettings({ ...storeSettings, store_name: e.target.value })}
                        className="w-full bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg px-3 py-2 text-zinc-800 font-semibold focus:border-[#E10600] focus:ring-1 focus:ring-[#E10600]/30 outline-none transition" 
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 uppercase font-bold tracking-wider text-[9px] block mb-1.5">WhatsApp Customer Support</label>
                      <input 
                        type="text" 
                        value={storeSettings.whatsapp_support} 
                        onChange={(e) => setStoreSettings({ ...storeSettings, whatsapp_support: e.target.value })}
                        className="w-full bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg px-3 py-2 text-zinc-800 font-semibold focus:border-[#E10600] focus:ring-1 focus:ring-[#E10600]/30 outline-none transition" 
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 uppercase font-bold tracking-wider text-[9px] block mb-1.5">UPI Payment Address</label>
                      <input 
                        type="text" 
                        value={storeSettings.upi_id} 
                        onChange={(e) => setStoreSettings({ ...storeSettings, upi_id: e.target.value })}
                        className="w-full bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg px-3 py-2 text-zinc-800 font-mono focus:border-[#E10600] focus:ring-1 focus:ring-[#E10600]/30 outline-none transition" 
                      />
                    </div>
                    
                    {/* QR Code Upload */}
                    <div>
                      <span className="text-zinc-400 uppercase font-bold tracking-wider text-[9px] block mb-1.5">UPI Payment QR Code</span>
                      
                      {storeSettings.upi_qr_url ? (
                        <div className="space-y-2 text-left">
                          <img 
                            src={storeSettings.upi_qr_url} 
                            alt="UPI QR Code Preview" 
                            className="w-32 h-32 object-contain border border-[#E5E5E5] rounded-lg p-1 bg-white" 
                          />
                          <button
                            type="button"
                            onClick={() => setStoreSettings({ ...storeSettings, upi_qr_url: "" })}
                            className="px-3 py-1 bg-red-50 hover:bg-red-100 text-red-600 font-bold rounded-lg text-[9px] uppercase tracking-wider transition cursor-pointer border border-red-100"
                          >
                            Delete QR Code
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <input 
                            type="file" 
                            accept="image/*" 
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                  if (file.size > 200 * 1024) {
                                    toast.error("QR Code image must be smaller than 200KB");
                                    return;
                                  }
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setStoreSettings({ ...storeSettings, upi_qr_url: reader.result });
                                  };
                                  reader.readAsDataURL(file);
                              }
                            }}
                            className="w-full text-zinc-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-[10px] file:font-black file:uppercase file:bg-zinc-100 file:text-zinc-700 hover:file:bg-zinc-200 transition cursor-pointer text-[10px]" 
                          />
                          <span className="text-[10px] text-zinc-400">Supported formats: JPG, PNG. Max size 200KB.</span>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      disabled={settingsLoading}
                      onClick={handleSaveSettings}
                      className="w-full py-2 bg-[#E10600] hover:bg-[#C80500] disabled:opacity-50 text-white font-bold rounded-lg uppercase tracking-wider text-[10px] transition cursor-pointer flex items-center justify-center min-h-[36px]"
                    >
                      {settingsLoading ? "Saving..." : "Save Configurations"}
                    </button>
                  </div>
                </div>

                {/* API Info settings */}
                <div className="bg-white border border-[#E5E5E5] rounded-[16px] p-6 shadow-xs">
                  <h3 className="text-xs font-black uppercase tracking-wider text-zinc-800 mb-4 border-b border-zinc-100 pb-2 flex items-center gap-1.5"><Info className="w-4 h-4" /> Environment parameters</h3>
                  <div className="space-y-3.5 text-xs">
                    <div>
                      <span className="text-zinc-400 uppercase font-bold tracking-wider text-[9px] block mb-1">Server Endpoint URL</span>
                      <div className="flex items-center gap-1.5 font-mono select-all bg-zinc-50 px-2.5 py-1.5 rounded-lg border border-zinc-200/50 break-all text-left">
                        <span className="flex-1">{API}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(API);
                            toast.success("API Endpoint URL copied!");
                          }}
                          className="p-1 hover:bg-zinc-200 rounded text-zinc-500 hover:text-zinc-800 shrink-0 transition"
                          title="Copy API URL"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <span className="text-zinc-400 uppercase font-bold tracking-wider text-[9px] block mb-1">System Status</span>
                      <span className="text-emerald-600 font-bold uppercase text-[10px] bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                        Operational & Secure
                      </span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* 12. ACTIVITY LOGS */}
          {activeTab === "activity" && (
            <div className="space-y-6 animate-fade-in text-left select-none">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">System Activity Logs</h1>
                  <p className="text-xs text-zinc-500 mt-1">Live audit trail of admin modifications, order state changes, and operations.</p>
                </div>
                <button
                  onClick={handleRefreshLogs}
                  disabled={loadingLogs}
                  className="px-3.5 py-1.5 bg-zinc-100 hover:bg-zinc-200 disabled:opacity-50 text-zinc-700 rounded-lg text-xs font-bold uppercase tracking-wider transition flex items-center gap-1.5 cursor-pointer min-h-[36px]"
                >
                  <ArrowClockwise className={`w-3.5 h-3.5 ${loadingLogs ? "animate-spin" : ""}`} />
                  {loadingLogs ? "Refreshing..." : "Refresh Logs"}
                </button>
              </div>

              {/* Search and Filters Bar */}
              <div className="flex flex-col sm:flex-row gap-3.5 bg-white border border-[#E5E5E5] rounded-2xl p-4 sm:p-5 shadow-xs">
                <div className="flex-1 flex gap-2">
                  <input
                    type="text"
                    placeholder="Search logs by action, description..."
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") fetchActivityLogs(1);
                    }}
                    className="flex-1 min-h-[38px] bg-[#F7F7F8] border border-[#E5E5E5] rounded-xl px-3.5 py-1.5 text-xs text-zinc-800 focus:outline-none focus:ring-1 focus:ring-[#E10600]"
                  />
                  <button
                    onClick={() => fetchActivityLogs(1)}
                    className="px-4 py-1.5 bg-zinc-850 hover:bg-zinc-950 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition cursor-pointer min-h-[38px]"
                  >
                    Search
                  </button>
                </div>

                <div className="flex gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-zinc-400 font-bold uppercase select-none">Resource</label>
                    <select
                      value={logFilterResourceType}
                      onChange={(e) => setLogFilterResourceType(e.target.value)}
                      className="min-h-[38px] bg-[#F7F7F8] border border-[#E5E5E5] rounded-xl px-3 py-1.5 text-xs text-zinc-700 focus:outline-none cursor-pointer font-medium"
                    >
                      <option value="">All Resources</option>
                      <option value="GAMES">Games</option>
                      <option value="BANNERS">Banners</option>
                      <option value="ORDERS">Orders</option>
                      <option value="REVIEWS">Reviews</option>
                      <option value="CATEGORIES">Categories</option>
                      <option value="SETTINGS">Settings</option>
                      <option value="AUTHENTICATION">Authentication</option>
                    </select>
                  </div>
                </div>
              </div>

              {loadingLogs ? (
                <div className="py-20 flex justify-center items-center bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">
                  <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="py-20 text-center select-none text-zinc-400 text-xs bg-white border border-[#E5E5E5] rounded-2xl shadow-xs">
                  No activity recorded yet.
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Desktop/Tablet Table */}
                  <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-[#E5E5E5] text-left text-xs">
                        <thead>
                          <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                            <th className="py-3.5 px-6">Action</th>
                            <th className="py-3.5 px-6">Target Resource</th>
                            <th className="py-3.5 px-6">Operator</th>
                            <th className="py-3.5 px-6">Timestamp</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {activityLogs.map(log => (
                            <tr key={log.id} className="hover:bg-zinc-50/50 transition duration-150">
                              <td className="py-4 px-6 font-bold text-zinc-900">
                                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                                  log.type === "success" ? "bg-emerald-500" : log.type === "warning" ? "bg-amber-500" : "bg-blue-500"
                                }`} />
                                {log.action}
                              </td>
                              <td className="py-4 px-6 text-zinc-700 font-semibold">{log.target}</td>
                              <td className="py-4 px-6 text-zinc-500 font-mono text-[11px]">{log.user}</td>
                              <td className="py-4 px-6 text-zinc-400 font-medium">
                                {new Date(log.timestamp).toLocaleDateString()} · {new Date(log.timestamp).toLocaleTimeString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards Layout */}
                  <div className="flex flex-col gap-3.5 md:hidden">
                    {activityLogs.map(log => (
                      <div key={log.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs text-xs text-left space-y-3">
                        <div className="flex justify-between items-center border-b border-zinc-100 pb-2">
                          <span className="font-bold text-zinc-900 flex items-center">
                            <span className={`inline-block w-2 h-2 rounded-full mr-2 shrink-0 ${
                              log.type === "success" ? "bg-emerald-500" : log.type === "warning" ? "bg-amber-500" : "bg-blue-500"
                            }`} />
                            {log.action}
                          </span>
                          <span className="text-[10px] text-zinc-400 font-semibold shrink-0">
                            {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 bg-zinc-50 border border-zinc-100 rounded-xl p-3">
                          <div>
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block">Target Resource</span>
                            <span className="text-zinc-800 font-bold break-all block mt-0.5">{log.target}</span>
                          </div>
                          <div>
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block">Operator</span>
                            <span className="text-zinc-600 font-mono text-[11px] truncate block mt-0.5">{log.user}</span>
                          </div>
                          <div className="col-span-2 border-t border-zinc-200/60 pt-2">
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block">Timestamp</span>
                            <span className="text-zinc-600 font-medium text-[10px]">
                              {new Date(log.timestamp).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' })} · {new Date(log.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination Footer */}
                  {logTotalPages > 1 && (
                    <div className="flex justify-between items-center px-5 py-3.5 bg-white border border-[#E5E5E5] rounded-2xl shadow-xs select-none">
                      <button
                        disabled={logPage <= 1}
                        onClick={() => fetchActivityLogs(logPage - 1)}
                        className="px-3.5 py-1.5 bg-zinc-50 hover:bg-zinc-100 disabled:opacity-50 border border-[#E5E5E5] rounded-xl text-xs font-bold text-zinc-600 transition cursor-pointer"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-zinc-500 font-bold">
                        Page {logPage} of {logTotalPages}
                      </span>
                      <button
                        disabled={logPage >= logTotalPages}
                        onClick={() => fetchActivityLogs(logPage + 1)}
                        className="px-3.5 py-1.5 bg-zinc-50 hover:bg-zinc-100 disabled:opacity-50 border border-[#E5E5E5] rounded-xl text-xs font-bold text-zinc-600 transition cursor-pointer"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 13. ADMIN USERS */}
          {activeTab === "admins" && (
            <div className="space-y-6 animate-fade-in text-left">
              <div>
                <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Console Administrators</h1>
                <p className="text-xs text-zinc-500 mt-1">Review active store managers with administrative privileges.</p>
              </div>

              {loadingUsers ? (
                <div className="bg-white border border-[#E5E5E5] rounded-2xl p-16 flex justify-center items-center shadow-xs">
                  <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                </div>
              ) : adminUsers.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] rounded-2xl p-12 text-center select-none text-zinc-400 text-xs shadow-xs">
                  No admin accounts configured.
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Desktop/Tablet Table */}
                  <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-[#E5E5E5] text-left text-xs">
                        <thead>
                          <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                            <th className="py-3.5 px-6">Name</th>
                            <th className="py-3.5 px-6">Email Address</th>
                            <th className="py-3.5 px-6">Permission Role</th>
                            <th className="py-3.5 px-6">Created Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {adminUsers.map(adm => (
                            <tr key={adm.id} className="hover:bg-zinc-50/50 transition duration-150">
                              <td className="py-4 px-6 font-bold text-zinc-800">{adm.full_name || "Verified Customer"}</td>
                              <td className="py-4 px-6 text-zinc-600 font-medium">{adm.email}</td>
                              <td className="py-4 px-6 text-[#E10600] font-black uppercase tracking-wider text-[10px]">
                                {adm.role || "admin"}
                              </td>
                              <td className="py-4 px-6 text-zinc-500 font-medium">
                                {adm.created_at ? new Date(adm.created_at).toLocaleDateString() : "Not specified"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards Layout */}
                  <div className="flex flex-col gap-3.5 md:hidden">
                    {adminUsers.map(adm => (
                      <div key={adm.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs text-xs text-left space-y-3">
                        <div className="flex items-start justify-between gap-2 border-b border-[#F0F0F0] pb-2.5">
                          <div className="min-w-0">
                            <h4 className="font-bold text-zinc-950 text-sm leading-tight truncate">{adm.full_name || "Verified Customer"}</h4>
                            <span className="text-[10px] text-zinc-400 font-mono mt-0.5 block truncate">{adm.email}</span>
                          </div>
                          <span className="text-[#E10600] font-black uppercase tracking-wider text-[9px] bg-red-50 border border-red-100 px-2.5 py-0.5 rounded-full shrink-0">
                            {adm.role || "admin"}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-[11px] bg-zinc-50 border border-zinc-100 rounded-xl p-2.5">
                          <div>
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block">Role</span>
                            <span className="font-extrabold text-zinc-800 uppercase tracking-wider text-[10px]">{adm.role || "Admin"}</span>
                          </div>
                          <div>
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block">Created Date</span>
                            <span className="font-semibold text-zinc-700 text-[10px]">{adm.created_at ? new Date(adm.created_at).toLocaleDateString() : "Not specified"}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 14. HOMEPAGE BANNERS */}
          {activeTab === "banners" && (
            <div className="space-y-6 animate-fade-in text-left">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none">
                <div>
                  <h1 className="text-2xl font-black text-zinc-900 uppercase tracking-tight">Homepage Banners</h1>
                  <p className="text-xs text-zinc-500 mt-1">Manage homepage carousel slides and promo cards.</p>
                </div>
                <button
                  onClick={() => {
                    const bannersCat = categories.find(c => c.name.toLowerCase() === "banners");
                    setEditingGame(null);
                    setGameFormData({
                      title: '',
                      description: '',
                      steam_price: '',
                      price: '0',
                      category_id: bannersCat?.id?.toString() || '',
                      platform: 'Steam',
                      image_url: '',
                      is_new: false,
                      is_bundle: false,
                      in_stock: true,
                      display_order: '1',
                      is_structured: false,
                      overview: '',
                      story: '',
                      gameplay: '',
                      features: ['']
                    });
                    setShowGameModal(true);
                  }}
                  className="bg-[#E10600] hover:bg-[#C80500] text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-transform hover:scale-102 flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <Plus className="w-4 h-4" /> Add Banner
                </button>
              </div>

              {loadingGames ? (
                <div className="bg-white border border-[#E5E5E5] rounded-2xl p-16 flex justify-center items-center shadow-xs">
                  <ArrowClockwise className="w-8 h-8 text-[#E10600] animate-spin" />
                </div>
              ) : (() => {
                const bannerGames = games.filter(g => 
                  g.categories?.name?.toLowerCase() === "banners" || 
                  g.categories?.name?.toLowerCase() === "promo banners"
                );

                if (bannerGames.length === 0) {
                  return (
                    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-12 text-center select-none text-zinc-400 text-xs shadow-xs">
                      No custom homepage banners or promo cards uploaded yet.
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    {/* Desktop/Tablet Table */}
                    <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden shadow-xs">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-[#E5E5E5] text-left text-xs">
                          <thead>
                            <tr className="text-[10px] text-zinc-500 bg-zinc-50 font-black uppercase tracking-wider select-none border-b border-[#E5E5E5]">
                              <th className="py-3.5 px-6">Image</th>
                              <th className="py-3.5 px-6">Banner Name</th>
                              <th className="py-3.5 px-6">Banner Type</th>
                              <th className="py-3.5 px-6">Target Redirect Link</th>
                              <th className="py-3.5 px-6">Priority</th>
                              <th className="py-3.5 px-6 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-zinc-100">
                            {bannerGames.map(game => (
                              <tr key={game.id} className="hover:bg-zinc-50/50 transition duration-150">
                                <td className="py-4 px-6 shrink-0 select-none">
                                  <img src={game.image_url} alt="" className="w-16 h-10 object-cover rounded-lg bg-zinc-100 border border-zinc-200/50" />
                                </td>
                                <td className="py-4 px-6">
                                  <span className="font-bold text-zinc-800 text-sm block">{game.title}</span>
                                </td>
                                <td className="py-4 px-6">
                                  <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                    game.categories?.name?.toLowerCase() === "banners" 
                                      ? "bg-red-50 text-[#E10600] border border-red-100" 
                                      : "bg-blue-50 text-blue-600 border border-blue-100"
                                  }`}>
                                    {game.categories?.name?.toLowerCase() === "banners" ? "Hero Carousel" : "Promo Grid"}
                                  </span>
                                </td>
                                <td className="py-4 px-6 font-mono text-zinc-500 text-xs break-all">
                                  {game.description || <span className="text-zinc-300 italic">None (Defaults to product page)</span>}
                                </td>
                                <td className="py-4 px-6 text-zinc-600 font-bold text-xs">
                                  {game.display_order ?? 0}
                                </td>
                                <td className="py-4 px-6 text-center">
                                  <div className="flex justify-center items-center gap-2">
                                    <button 
                                      onClick={() => handleEditGame(game)}
                                      className="p-2 hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 rounded-lg transition"
                                      title="Edit banner"
                                      aria-label="Edit banner"
                                    >
                                      <PencilSimple className="w-4 h-4" />
                                    </button>
                                    <button 
                                      onClick={() => handleDeleteGame(game.id)}
                                      className="p-2 hover:bg-red-50 text-red-500 hover:text-red-700 rounded-lg transition"
                                      title="Delete banner"
                                      aria-label="Delete banner"
                                    >
                                      <Trash className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile Cards Layout */}
                    <div className="flex flex-col gap-3.5 md:hidden">
                      {bannerGames.map(game => (
                        <div key={game.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-xs text-xs text-left space-y-3">
                          <div className="flex items-start gap-3">
                            <img 
                              src={game.image_url} 
                              alt="" 
                              className="w-20 h-14 object-cover rounded-xl bg-zinc-100 border border-zinc-200/60 shrink-0" 
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-black text-zinc-950 text-sm leading-tight line-clamp-2">{game.title}</h4>
                              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                  game.categories?.name?.toLowerCase() === "banners" 
                                    ? "bg-red-50 text-[#E10600] border border-red-100" 
                                    : "bg-blue-50 text-blue-600 border border-blue-100"
                                }`}>
                                  {game.categories?.name?.toLowerCase() === "banners" ? "Hero Carousel" : "Promo Grid"}
                                </span>
                                <span className="text-[10px] text-zinc-500 font-bold bg-zinc-100 px-2 py-0.5 rounded-md">Priority #{game.display_order ?? 0}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-zinc-50 border border-zinc-100 rounded-xl p-2.5">
                            <span className="text-zinc-400 font-bold uppercase text-[9px] block mb-0.5">Target Redirect Link</span>
                            <span className="font-mono text-zinc-800 text-[11px] break-all">{game.description || <span className="text-zinc-400 italic">None (Defaults to product page)</span>}</span>
                          </div>
                          
                          <div className="flex justify-end gap-2 pt-1 border-t border-[#F0F0F0]">
                            <button 
                              onClick={() => handleEditGame(game)}
                              className="flex-1 py-2 bg-zinc-50 hover:bg-zinc-100 text-zinc-700 font-bold rounded-xl transition border border-zinc-200/80 flex items-center justify-center gap-1.5 cursor-pointer text-xs"
                              title="Edit banner"
                            >
                              <PencilSimple className="w-4 h-4" /> Edit
                            </button>
                            <button 
                              onClick={() => handleDeleteGame(game.id)}
                              className="p-2 bg-red-50/50 hover:bg-red-50 text-red-600 font-bold rounded-xl transition border border-red-100 flex items-center justify-center cursor-pointer"
                              title="Delete banner"
                              aria-label="Delete banner"
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

        </main>
      </div>

      {/* ── MOBILE SIDEBAR DRAWER MENU ── */}
      {showMobileSidebar && (
        <div className="fixed inset-0 z-50 flex md:hidden animate-fade-in">
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity duration-300"
            onClick={() => setShowMobileSidebar(false)}
          />
          <div className="relative w-66 bg-white h-[100dvh] max-h-[100dvh] shadow-2xl flex flex-col py-0 z-10 animate-slide-in-left overflow-hidden pb-safe">
            {renderSidebarContent(true)}
          </div>
        </div>
      )}

      {/* ── GAME ADD/EDIT MODAL ── */}
      {showGameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto animate-fade-in select-none">
          <div className="absolute inset-0 bg-black/55 backdrop-blur-xs" onClick={() => setShowGameModal(false)} />
          <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-xl w-full max-w-lg relative z-10 overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-[#E5E5E5] flex justify-between items-center bg-zinc-50 text-left shrink-0">
              <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-800">
                {editingGame ? "Edit Catalog Item" : "Create Catalog Item"}
              </h3>
              <button onClick={() => setShowGameModal(false)} className="p-1 hover:bg-zinc-200 rounded-lg transition text-zinc-500 hover:text-zinc-800">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body / Form */}
            <form onSubmit={handleSubmitGame} className="flex-1 overflow-y-auto p-5 space-y-4 text-left text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                
                {/* 1. PLATFORM & ACCOUNT TYPE (Platform-First Architecture) */}
                {(() => {
                  const selectedCat = categories.find(c => c.id.toString() === gameFormData.category_id?.toString());
                  const isBanner = selectedCat && (selectedCat.name.toLowerCase() === "banners" || selectedCat.name.toLowerCase() === "promo banners");
                  
                  if (isBanner) return null;

                  return (
                    <div className="sm:col-span-2 bg-zinc-50 border border-zinc-200/80 rounded-2xl p-4 space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-zinc-600 font-black uppercase text-[10px] tracking-wider block">1. Platform *</label>
                          <span className="text-[10px] text-zinc-400 font-semibold">10 Platforms Registered</span>
                        </div>
                        <select
                          required
                          value={gameFormData.platform_id || ""}
                          onChange={(e) => {
                            const selectedId = e.target.value;
                            const matched = platforms.find(p => p.id === selectedId);
                            setGameFormData({ 
                              ...gameFormData, 
                              platform_id: selectedId,
                              platform: matched ? matched.name : "Steam"
                            });
                          }}
                          className="w-full h-11 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-xl px-3.5 text-zinc-900 bg-white font-bold uppercase tracking-wider transition shadow-2xs"
                        >
                          <option value="" disabled>Select Target Platform</option>
                          {platforms.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name} {p.is_locked ? "(Locked Platform)" : ""}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Read-Only Derived Account Type & Delivery Spec */}
                      <div className="bg-white border border-zinc-200/60 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">2. Product / Account Type:</span>
                          <span className="font-extrabold text-zinc-900 uppercase tracking-wider text-[11px] bg-zinc-100 px-2 py-0.5 rounded">
                            {gameFormData.platform ? getPlatformAccountType(gameFormData.platform) : "Account"}
                          </span>
                        </div>
                        {gameFormData.platform && (
                          <div className="flex items-center gap-1.5 text-zinc-500 text-[10px] font-medium">
                            <PlatformBadge platform={gameFormData.platform} showName={true} className="scale-85 origin-left" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* 2. Category / Genre */}
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">Category / Genre *</label>
                  <select
                    required
                    value={gameFormData.category_id}
                    onChange={(e) => setGameFormData({ ...gameFormData, category_id: e.target.value })}
                    className="w-full h-10 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg px-3 text-zinc-800 bg-white font-bold uppercase tracking-wider transition"
                  >
                    {categories
                      .filter(c => {
                        const isBannerCat = c.name.toLowerCase() === "banners" || c.name.toLowerCase() === "promo banners";
                        return activeTab === "banners" ? isBannerCat : !isBannerCat;
                      })
                      .map(c => (
                        <option key={c.id} value={c.id.toString()}>{c.name}</option>
                      ))
                    }
                  </select>
                </div>

                {/* 3. Game Title */}
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">
                    {(() => {
                      const selectedCat = categories.find(c => c.id.toString() === gameFormData.category_id?.toString());
                      const isBanner = selectedCat && (selectedCat.name.toLowerCase() === "banners" || selectedCat.name.toLowerCase() === "promo banners");
                      return isBanner ? "Banner Title / Name (For Admin Identification) *" : "Game Title *";
                    })()}
                  </label>
                  <input
                    type="text"
                    required
                    value={gameFormData.title}
                    onChange={(e) => setGameFormData({ ...gameFormData, title: e.target.value })}
                    className="w-full h-10 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg px-3.5 text-zinc-800 placeholder-zinc-400 bg-white font-semibold transition"
                    placeholder={(() => {
                      const selectedCat = categories.find(c => c.id.toString() === gameFormData.category_id?.toString());
                      const isBanner = selectedCat && (selectedCat.name.toLowerCase() === "banners" || selectedCat.name.toLowerCase() === "promo banners");
                      return isBanner ? "e.g. Slide 1 Summer Deals" : "Grand Theft Auto V";
                    })()}
                  />
                </div>

                {/* 4. Pricing (Sale Price & Steam Price) */}
                {(() => {
                  const selectedCat = categories.find(c => c.id.toString() === gameFormData.category_id?.toString());
                  const isBanner = selectedCat && (selectedCat.name.toLowerCase() === "banners" || selectedCat.name.toLowerCase() === "promo banners");
                  
                  if (isBanner) return null;

                  return (
                    <>
                      <div>
                        <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">Sale Price (₹) *</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={gameFormData.price}
                          onChange={(e) => setGameFormData({ ...gameFormData, price: e.target.value })}
                          className="w-full h-10 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg px-3.5 text-zinc-800 bg-white font-semibold transition"
                          placeholder="99.00"
                        />
                      </div>

                      <div>
                        <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">Steam Price / MRP (₹) [Crossed]</label>
                        <input
                          type="number"
                          step="0.01"
                          value={gameFormData.steam_price}
                          onChange={(e) => setGameFormData({ ...gameFormData, steam_price: e.target.value })}
                          className="w-full h-10 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg px-3.5 text-zinc-800 bg-white font-semibold transition"
                          placeholder="999.00"
                        />
                      </div>
                    </>
                  );
                })()}

                {/* 5. Display Priority */}
                <div>
                  <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">
                    Display Priority <span className="text-zinc-300 normal-case font-medium">(1 = top priority)</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="9999"
                    value={gameFormData.display_order}
                    onChange={(e) => setGameFormData({ ...gameFormData, display_order: e.target.value })}
                    className="w-full h-10 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg px-3.5 text-zinc-800 bg-white font-semibold transition"
                    placeholder="e.g. 1 = first, 50 = popular"
                  />
                </div>

                {/* 6. Cover Image */}
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">Cover Image</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={gameFormData.image_url}
                      onChange={(e) => setGameFormData({ ...gameFormData, image_url: e.target.value })}
                      className="flex-1 h-10 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg px-3.5 text-zinc-800 placeholder-zinc-400 bg-white font-semibold transition"
                      placeholder="Paste image URL or upload file..."
                    />
                    <label className="h-10 bg-zinc-100 hover:bg-zinc-200 border border-zinc-300 text-zinc-700 hover:text-zinc-900 rounded-lg px-4 flex items-center justify-center font-bold text-xs uppercase tracking-wider transition cursor-pointer select-none">
                      <span>{uploadingImage ? "..." : "Upload"}</span>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingImage}
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {uploadingImage && <span className="text-[10px] text-[#E10600] font-bold mt-1 block">Uploading image...</span>}
                </div>

                {/* 7. Stock Checkboxes */}
                {(() => {
                  const selectedCat = categories.find(c => c.id.toString() === gameFormData.category_id?.toString());
                  const isBanner = selectedCat && (selectedCat.name.toLowerCase() === "banners" || selectedCat.name.toLowerCase() === "promo banners");
                  
                  if (isBanner) return null;

                  return (
                    <div className="sm:col-span-2 flex flex-wrap gap-5 select-none border border-zinc-100 p-3 rounded-xl bg-zinc-50/50">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="checkbox"
                          checked={gameFormData.in_stock}
                          onChange={(e) => setGameFormData({ ...gameFormData, in_stock: e.target.checked })}
                          className="w-4 h-4 accent-[#E10600]"
                        />
                        <span className="font-bold text-zinc-700">In Stock Status</span>
                      </label>
                      
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="checkbox"
                          checked={gameFormData.is_new}
                          onChange={(e) => setGameFormData({ ...gameFormData, is_new: e.target.checked })}
                          className="w-4 h-4 accent-[#E10600]"
                        />
                        <span className="font-bold text-zinc-700">New Release Badge</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="checkbox"
                          checked={gameFormData.is_bundle}
                          onChange={(e) => setGameFormData({ ...gameFormData, is_bundle: e.target.checked })}
                          className="w-4 h-4 accent-[#E10600]"
                        />
                        <span className="font-bold text-zinc-700">Bundle Offer</span>
                      </label>
                    </div>
                  );
                })()}

                {/* 8. Description (Simple or Structured) */}
                {(() => {
                  const selectedCat = categories.find(c => c.id.toString() === gameFormData.category_id?.toString());
                  const isBanner = selectedCat && (selectedCat.name.toLowerCase() === "banners" || selectedCat.name.toLowerCase() === "promo banners");
                  
                  if (isBanner) {
                    return (
                      <div className="sm:col-span-2">
                        <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">Target Redirect Link (Optional)</label>
                        <input
                          type="text"
                          value={gameFormData.description || ""}
                          onChange={(e) => setGameFormData({ ...gameFormData, description: e.target.value })}
                          className="w-full h-10 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg px-3.5 text-zinc-800 placeholder-zinc-400 bg-white font-semibold transition"
                          placeholder="e.g. /games?platform=Steam or /game/[id] or custom link"
                        />
                      </div>
                    );
                  }

                  return (
                    <div className="sm:col-span-2 space-y-3">
                      <div className="flex items-center justify-between bg-zinc-50 border border-[#E5E5E5] p-3 rounded-xl select-none">
                        <div>
                          <span className="text-zinc-700 font-bold block">Structured Description Details</span>
                          <span className="text-zinc-400 text-[10px] block font-medium">Write detailed Overview, Story, Gameplay, and Key Features</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setGameFormData({ ...gameFormData, is_structured: !gameFormData.is_structured })}
                          className={`w-11 h-6 rounded-full transition-colors relative flex items-center px-1 shrink-0 ${gameFormData.is_structured ? 'bg-[#E10600]' : 'bg-zinc-300'}`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${gameFormData.is_structured ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                      </div>

                      {!gameFormData.is_structured ? (
                        <div>
                          <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">Simple Description</label>
                          <textarea
                            value={gameFormData.description}
                            onChange={(e) => setGameFormData({ ...gameFormData, description: e.target.value })}
                            className="w-full h-24 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg p-3 text-zinc-800 placeholder-zinc-400 bg-white font-semibold transition"
                            placeholder="Game summary details..."
                          />
                        </div>
                      ) : (
                        <div className="space-y-3 bg-zinc-50/50 border border-[#E5E5E5] p-4 rounded-xl">
                          <div>
                            <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">Overview (Intro) *</label>
                            <textarea
                              required={gameFormData.is_structured}
                              value={gameFormData.overview}
                              onChange={(e) => setGameFormData({ ...gameFormData, overview: e.target.value })}
                              className="w-full h-20 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg p-3 text-zinc-800 placeholder-zinc-400 bg-white font-semibold transition"
                              placeholder="Brief 2-4 sentences explaining what the game is..."
                            />
                          </div>
                          
                          <div>
                            <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">Story & Setting</label>
                            <textarea
                              value={gameFormData.story}
                              onChange={(e) => setGameFormData({ ...gameFormData, story: e.target.value })}
                              className="w-full h-20 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg p-3 text-zinc-800 placeholder-zinc-400 bg-white font-semibold transition"
                              placeholder="Explain the game's world, setting, and basic premise..."
                            />
                          </div>

                          <div>
                            <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">Gameplay Experience</label>
                            <textarea
                              value={gameFormData.gameplay}
                              onChange={(e) => setGameFormData({ ...gameFormData, gameplay: e.target.value })}
                              className="w-full h-20 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg p-3 text-zinc-800 placeholder-zinc-400 bg-white font-semibold transition"
                              placeholder="Explain the core gameplay loop and mechanics..."
                            />
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="text-zinc-400 font-bold uppercase text-[9px] block">Key Features</label>
                              <button
                                type="button"
                                onClick={() => setGameFormData({ ...gameFormData, features: [...gameFormData.features, ''] })}
                                className="text-[10px] font-bold text-[#E10600] hover:text-[#C10500] uppercase tracking-wider transition"
                              >
                                + Add Feature
                              </button>
                            </div>
                            
                            <div className="space-y-2">
                              {gameFormData.features.map((feature, idx) => (
                                <div key={idx} className="flex gap-2">
                                  <input
                                    type="text"
                                    value={feature}
                                    onChange={(e) => {
                                      const updated = [...gameFormData.features];
                                      updated[idx] = e.target.value;
                                      setGameFormData({ ...gameFormData, features: updated });
                                    }}
                                    className="flex-1 h-9 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg px-3 text-zinc-800 placeholder-zinc-400 bg-white font-semibold transition"
                                    placeholder={`Feature #${idx + 1}`}
                                  />
                                  {gameFormData.features.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updated = gameFormData.features.filter((_, i) => i !== idx);
                                        setGameFormData({ ...gameFormData, features: updated });
                                      }}
                                      className="px-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-500 rounded-lg text-[10px] font-bold transition uppercase"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Modal Actions */}
              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-3.5 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowGameModal(false)}
                  className="px-4 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-lg font-bold uppercase tracking-wider cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#E10600] hover:bg-[#C80500] text-white rounded-lg font-bold uppercase tracking-wider cursor-pointer"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── CATEGORY MODAL ── */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in select-none">
          <div className="absolute inset-0 bg-black/55 backdrop-blur-xs" onClick={() => setShowCategoryModal(false)} />
          <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-xl w-full max-w-sm relative z-10 overflow-hidden text-left flex flex-col">
            <div className="px-5 py-4 border-b border-[#E5E5E5] flex justify-between items-center bg-zinc-50 shrink-0">
              <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-800">
                {editingCategory ? "Rename Category" : "Add New Category"}
              </h3>
              <button onClick={() => setShowCategoryModal(false)} className="p-1 hover:bg-zinc-200 rounded-lg transition text-zinc-500 hover:text-zinc-800">
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <form onSubmit={handleSaveCategory} className="p-5 space-y-4">
              <div>
                <label className="text-zinc-400 font-bold uppercase text-[9px] block mb-1">Category name *</label>
                <input
                  type="text"
                  required
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  className="w-full h-10 border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg px-3.5 text-zinc-800 placeholder-zinc-400 bg-white font-semibold transition"
                  placeholder="e.g. Action Adventure"
                />
              </div>

              <div className="pt-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(false)}
                  className="px-4 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-lg font-bold uppercase tracking-wider cursor-pointer text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loadingCategories}
                  className="px-5 py-2 bg-[#E10600] hover:bg-[#C80500] text-white rounded-lg font-bold uppercase tracking-wider cursor-pointer text-xs disabled:opacity-50"
                >
                  {loadingCategories ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ORDER DETAILS DRAWER ── */}
      {showOrderDrawer && selectedOrder && (
        <div className="fixed inset-0 z-50 flex justify-end animate-fade-in select-none">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={() => setShowOrderDrawer(false)} />
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col z-10 overflow-y-auto animate-slide-in-right p-6 text-left">
            
            {/* Header */}
            <div className="flex justify-between items-center border-b border-[#E5E5E5] pb-4 mb-5">
              <div>
                <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Order Details</span>
                <h3 className="text-sm font-bold text-zinc-800 font-mono">#{selectedOrder.id.slice(0, 8)}</h3>
              </div>
              <button 
                onClick={() => setShowOrderDrawer(false)}
                className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-400 hover:text-zinc-800 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6 flex-1 text-xs">
              {/* Customer Info Card */}
              <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl p-5 space-y-4 text-left">
                <span className="text-[10px] font-black uppercase tracking-wider text-[#666666] block border-b border-[#E5E5E5] pb-2 select-none">
                  CUSTOMER INFO
                </span>
                
                <div className="space-y-3.5 sm:space-y-0 sm:grid sm:grid-cols-[140px_1fr] sm:gap-x-4 sm:gap-y-3">
                  {/* Billing Name */}
                  <div className="flex flex-col sm:contents">
                    <span className="text-[#666666] font-medium text-xs select-none">
                      Billing Name
                    </span>
                    <span className="text-[#111111] font-semibold text-xs mt-1 sm:mt-0" style={longValueStyle}>
                      {selectedOrder.billing_name || selectedOrder.profiles?.full_name || (
                        <span className="text-[#666666]/60 italic font-normal">Not specified</span>
                      )}
                    </span>
                  </div>

                  {/* Billing Email */}
                  <div className="flex flex-col sm:contents">
                    <span className="text-[#666666] font-medium text-xs select-none">
                      Billing Email
                    </span>
                    <span className="text-[#111111] font-semibold text-xs mt-1 sm:mt-0" style={longValueStyle}>
                      {selectedOrder.billing_email || selectedOrder.profiles?.email || (
                        <span className="text-[#666666]/60 italic font-normal">Not specified</span>
                      )}
                    </span>
                  </div>

                  {/* Contact Phone */}
                  <div className="flex flex-col sm:contents">
                    <span className="text-[#666666] font-medium text-xs select-none">
                      Contact Phone
                    </span>
                    <span className="text-[#111111] font-semibold text-xs mt-1 sm:mt-0" style={longValueStyle}>
                      {selectedOrder.billing_phone || (
                        <span className="text-[#666666]/60 italic font-normal">Not specified</span>
                      )}
                    </span>
                  </div>

                  {/* Address / Coordinates */}
                  <div className="flex flex-col sm:contents">
                    <span className="text-[#666666] font-medium text-xs select-none">
                      Address / Coordinates
                    </span>
                    <span className="text-[#111111] font-semibold text-xs mt-1 sm:mt-0" style={longValueStyle}>
                      {selectedOrder.billing_address || (
                        <span className="text-[#666666]/60 italic font-normal">Not specified</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Items List */}
              <div className="space-y-3">
                <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400 block">Ordered Items</span>
                <div className="space-y-2 max-h-[140px] overflow-y-auto pr-1">
                  {selectedOrder.order_items?.map(item => (
                    <div key={item.id} className="flex justify-between items-center p-2 border border-zinc-200/60 rounded-lg">
                      <div className="min-w-0">
                        <p className="font-bold text-zinc-800 truncate">{item.games?.title || "Game coordinate license"}</p>
                        <span className="text-[10px] text-zinc-400 font-semibold">Qty: {item.quantity}</span>
                      </div>
                      <span className="font-black text-zinc-900 ml-2">₹{item.price}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-[#E5E5E5] font-black text-sm">
                  <span className="text-zinc-800 uppercase text-xs">Total Amount</span>
                  <span className="text-zinc-900">₹{selectedOrder.total_price}</span>
                </div>
              </div>

              {/* Payment Proof verification */}
              <div className="bg-zinc-50 border border-zinc-200/50 rounded-xl p-4 space-y-3">
                <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400 block">UTR Reference verification</span>
                <div className="flex justify-between items-center">
                  <span className="text-zinc-500 font-bold">Reference Number:</span>
                  <span className="font-mono text-zinc-800 font-bold text-sm bg-zinc-100 px-2 py-0.5 rounded">{selectedOrder.transaction_id || "None Submitted"}</span>
                </div>

                {selectedOrder.transaction_id && selectedOrder.payment_status !== "paid" && selectedOrder.payment_status !== "verified" && selectedOrder.status !== "cancelled" && selectedOrder.status !== "completed" && (
                  <div className="flex gap-2.5 pt-2 select-none">
                    <button
                      disabled={orderActionLoading}
                      onClick={() => handleVerifyPayment(selectedOrder.id)}
                      className="flex-1 py-2 bg-[#16A34A] hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg uppercase tracking-wider text-[10px] transition cursor-pointer"
                    >
                      {orderActionLoading ? "Processing..." : "Verify (PAID)"}
                    </button>
                    <button
                      disabled={orderActionLoading}
                      onClick={() => handleRejectPayment(selectedOrder.id)}
                      className="py-2 px-3 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed text-[#DC2626] font-bold rounded-lg uppercase tracking-wider text-[10px] transition cursor-pointer"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>

              {/* Digital Activation details input */}
              {(selectedOrder.payment_status === "paid" || selectedOrder.payment_status === "verified" || selectedOrder.status === "processing" || selectedOrder.status === "delivery" || selectedOrder.status === "completed") && (
                <div className="bg-zinc-50 border border-zinc-200/50 rounded-xl p-4 space-y-3 select-none">
                  <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400 block">Digital key coordinates delivery</span>
                  <div>
                    <label className="text-zinc-500 font-bold uppercase text-[9px] block mb-1">Delivery Channel</label>
                    <select
                      value={deliveryMethod}
                      onChange={(e) => setDeliveryMethod(e.target.value)}
                      className="w-full h-8 border border-zinc-200 focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg px-2 text-zinc-800 font-semibold bg-white"
                    >
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="Email">Email</option>
                      <option value="Platform Account">Direct Platform account</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-zinc-500 font-bold uppercase text-[9px] block mb-1">Activation Details / Steam Coordinates</label>
                    <textarea
                      value={deliveryDetails}
                      onChange={(e) => setDeliveryDetails(e.target.value)}
                      placeholder="Input steam credentials, activation keys or delivery messages here..."
                      className="w-full h-16 border border-zinc-200 focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg p-2 text-zinc-800 font-semibold bg-white"
                    />
                  </div>
                  <button
                    disabled={orderActionLoading}
                    onClick={() => handleSaveDeliveryDetails(selectedOrder.id)}
                    className="w-full py-2 bg-[#E10600] hover:bg-[#C80500] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg uppercase tracking-wider text-[10px] transition cursor-pointer"
                  >
                    {orderActionLoading 
                      ? "Updating..." 
                      : (selectedOrder.status === "completed" || selectedOrder.status === "delivery")
                        ? "Update Delivery details"
                        : "Mark as Delivered & Send details"}
                  </button>

                  {selectedOrder.status === "delivery" && (
                    <button
                      disabled={orderActionLoading}
                      onClick={() => handleCompleteOrder(selectedOrder.id)}
                      className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg uppercase tracking-wider text-[10px] transition cursor-pointer mt-2"
                    >
                      {orderActionLoading ? "Processing..." : "Complete Order"}
                    </button>
                  )}
                </div>
              )}

              {/* Admin Notes */}
              <div className="bg-zinc-50 border border-zinc-200/50 rounded-xl p-4 space-y-2 select-none">
                <label className="text-[9px] font-black uppercase tracking-widest text-zinc-400 block mb-1">Private Admin Notes</label>
                <textarea
                  value={tempNotes}
                  onChange={(e) => setTempNotes(e.target.value)}
                  placeholder="Private comments, reference logs..."
                  className="w-full h-12 border border-zinc-200 focus:outline-none focus:ring-1 focus:ring-[#E10600] rounded-lg p-2 text-zinc-800 font-semibold bg-white"
                />
                <button
                  disabled={orderActionLoading}
                  onClick={() => handleSaveAdminNotes(selectedOrder.id)}
                  className="w-full py-1.5 bg-zinc-200 hover:bg-zinc-300 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-700 font-bold rounded-lg uppercase tracking-wider text-[9px] transition cursor-pointer"
                >
                  {orderActionLoading ? "Saving..." : "Save Notes"}
                </button>
              </div>

              {/* Destructive Actions */}
              {selectedOrder.status !== "cancelled" && selectedOrder.status !== "completed" && (
                <div className="pt-2 select-none">
                  <button
                    disabled={orderActionLoading}
                    onClick={() => handleCancelOrder(selectedOrder.id)}
                    className="w-full py-2 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed text-[#DC2626] font-bold rounded-lg uppercase tracking-wider text-[10px] transition cursor-pointer flex items-center justify-center gap-1"
                  >
                    <Trash className="w-3.5 h-3.5" /> Cancel Order
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Admin;
